import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Upload, Wand2, Sparkles, Share2, Loader2, Check, Copy, Download,
  Film, ArrowLeft, ShieldCheck, Clapperboard, Cpu, RefreshCw, Mic, Crop, Layers,
  Music2, Captions, Package, Shuffle, Rocket, AudioLines, Scissors, ArrowUp, ArrowDown, Volume2,
  Ratio, CalendarClock, Trash2, Disc3, MessageSquare, CalendarDays, List, FileAudio, Play,
  Type, Bookmark, Heart, ChevronLeft, ChevronRight, Plus, User, Pause, Image as ImageIcon,
  CheckCircle2, X, RotateCcw, Lock, Unlock, GripVertical, Pencil, Clock, Users, ChevronDown, Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { TopNav } from "@/components/TopNav";
import { ShareDialog } from "@/components/ShareDialog";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, exportUrl, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";

const PLATFORMS = [
  { key: "tiktok", label: "TikTok" },
  { key: "instagram", label: "Instagram" },
  { key: "youtube", label: "YouTube" },
  { key: "facebook", label: "Facebook" },
  { key: "twitter", label: "X / Twitter" },
  { key: "linkedin", label: "LinkedIn" },
];

const ENGINES = [
  { value: "openai", label: "OpenAI · GPT-5.4 + GPT Image" },
  { value: "gemini", label: "Gemini 3.1 Pro + Nano Banana" },
  { value: "anthropic", label: "Claude Sonnet" },
];

const VOICES = ["nova", "alloy", "fable", "onyx", "shimmer", "echo", "coral", "sage", "ash"];
const RATIOS = [
  { key: "9:16", label: "9:16 · Reels/Shorts/TikTok" },
  { key: "1:1", label: "1:1 · Feed square" },
  { key: "16:9", label: "16:9 · YouTube" },
];
const RENDER_VOICES = ["nova", "alloy", "fable", "onyx", "shimmer", "echo", "coral", "sage", "ash"];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
// Build the full narrator option list: standard OpenAI voices + African-accent presets +
// the user's cloned personal voice(s). Values are tokens the backend routes on.
const buildVoiceItems = (catalog, project) => {
  const items = RENDER_VOICES.map((v) => ({ value: v, label: cap(v) }));
  (catalog?.african?.voices || []).forEach((v) =>
    items.push({ value: `african:${v.id}`, label: `${v.name} · African` }));
  if (catalog?.personal?.available && catalog.personal.voice_id)
    items.push({ value: `personal:${catalog.personal.voice_id}`, label: "My Voice · cloned" });
  const ppv = project?.personal_voice;
  if (ppv?.voice_id && ppv.voice_id !== catalog?.personal?.voice_id)
    items.push({ value: `personal:${ppv.voice_id}`, label: "My Voice · this project" });
  return items;
};
const DEFAULT_STEPS = [
  { type: "intro", label: "Intro frame (3s)", enabled: true, asset_id: "", position: "top-right" },
  { type: "overlay", label: "Corner overlay", enabled: false, asset_id: "", position: "top-right" },
  { type: "subtitles", label: "Burn subtitles", enabled: true },
  { type: "audio", label: "Add voiceover audio", enabled: true },
];
const CAP_SCALE = { small: 0.72, medium: 1.0, large: 1.45 };
const PREVIEW_ASPECTS = { "9:16": "9 / 16", "1:1": "1 / 1", "16:9": "16 / 9" };
const parseTs = (ts) => {
  const p = String(ts || "0").split(":").map(Number);
  if (p.length === 3) return (p[0] || 0) * 3600 + (p[1] || 0) * 60 + (p[2] || 0);
  if (p.length === 2) return (p[0] || 0) * 60 + (p[1] || 0);
  return p[0] || 0;
};
const PresetChip = ({ preset }) => {
  const boxed = preset.highlight_style === "box" && preset.highlight_color;
  const style = boxed
    ? { backgroundColor: preset.highlight_color, color: "#151515", fontFamily: FONT_CSS[preset.font || "sans"] }
    : { color: preset.highlight_color || "#FFFFFF", fontFamily: FONT_CSS[preset.font || "sans"], border: "1px solid rgba(255,255,255,0.18)", backgroundColor: "#0b0b0b" };
  return (
    <span className="inline-flex items-center justify-center rounded px-1 h-4 min-w-[22px] text-[10px] font-bold shrink-0" style={style} data-testid={`preset-chip-${preset.id}`}>Aa</span>
  );
};
const FONT_CSS = {
  sans: "'DejaVu Sans', sans-serif",
  serif: "'DejaVu Serif', Georgia, serif",
  condensed: "'Liberation Sans Narrow', 'Arial Narrow', sans-serif",
  mono: "'DejaVu Sans Mono', monospace",
};
const OUTLINE_CSS = {
  none: { stroke: "0", shadow: "none" },
  light: { stroke: "0.5px", shadow: "0 1px 2px #151515" },
  medium: { stroke: "1px", shadow: "0 2px 6px #151515, 0 0 3px #151515" },
  heavy: { stroke: "2px", shadow: "0 0 3px #151515, 0 2px 8px #000000" },
};
// endpoint path -> server credit-cost key (must match backend CREDIT_COSTS)
const ENDPOINT_COST = {
  analyze: "analyze", "generate-graphics": "graphics", "generate-script": "script",
  subtitles: "subtitles", "voiceover-audio": "voiceover", transcribe: "transcribe",
  render: "render", resize: "resize", "reframe-all": "reframe_all",
  "cover-frame": "cover", "run-all": "run_all",
};
const LOW_CREDIT_THRESHOLD = 15;
const MAX_VIDEO_MB = 200;
const VIDEO_EXTS = ["mp4", "mov", "m4v", "webm", "mkv", "avi", "mpeg", "mpg", "wmv", "flv"];

export default function Studio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser, refreshUser } = useAuth();
  const fileRef = useRef(null);
  const [costs, setCosts] = useState(null);
  const [readyBanner, setReadyBanner] = useState(false);
  const [project, setProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const lastFileRef = useRef(null);
  const [busy, setBusy] = useState({});
  const [is3D, setIs3D] = useState(true);
  const [tab, setTab] = useState("story");

  // compose + regen local state
  const [composeAsset, setComposeAsset] = useState("");
  const [thumbAsset, setThumbAsset] = useState("");
  const [regen, setRegen] = useState(null); // {id, prompt}
  const [voice, setVoice] = useState("nova");
  const [ttsEngine, setTtsEngine] = useState("openai");
  const [elVoice, setElVoice] = useState("21m00Tcm4Tlm");
  const [voiceCatalog, setVoiceCatalog] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [compareMode, setCompareMode] = useState("after");
  const voiceFileRef = useRef(null);
  const reloadVoices = async () => {
    try { const { data } = await api.get("/voices"); setVoiceCatalog(data); } catch {}
  };
  useEffect(() => {
    api.get("/voices").then(({ data }) => setVoiceCatalog(data)).catch(() => {});
  }, []);

  // Preview any narrator voice token (openai name, `african:<id>` or `personal:<id>`)
  const previewVoiceToken = async (token) => {
    setPreviewing(true);
    try {
      let body;
      if (token?.startsWith("african:")) body = { engine: "african", voice_id: token.split(":")[1] };
      else if (token?.startsWith("personal:")) body = { engine: "personal", voice: token };
      else body = { engine: "openai", voice: token || "nova" };
      const { data } = await api.post("/voices/preview", body);
      if (auditionAudioRef.current) { auditionAudioRef.current.pause(); }
      auditionAudioRef.current = new Audio(fileUrl(data.storage_path));
      auditionAudioRef.current.play();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not preview voice.");
    } finally { setPreviewing(false); }
  };

  // Clone the user's own voice from an uploaded sample or a live recording (account-wide or this project only)
  const cloneScopeRef = useRef("account");
  const [voiceConsent, setVoiceConsent] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const mediaRecRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const clonePollRef = useRef(null);
  const auditionAudioRef = useRef(null);
  const pollCloneStatus = (scope) => {
    const tick = async () => {
      try {
        let st, pv;
        if (scope === "project") {
          const { data } = await api.get(`/projects/${id}`);
          st = data.voice_clone_status; pv = data.personal_voice;
        } else {
          const { data } = await api.get(`/voices/personal/status`);
          st = data.status; pv = data.personal_voice;
        }
        if (st && st.running === false) {
          clearInterval(clonePollRef.current);
          setBusyKey("clonevoice", false);
          if (st.error) {
            toast.error(st.error);
          } else {
            await reloadVoices();
            await load();
            refreshUser();
            if (pv?.voice_id) {
              toast.success("Your voice is ready! Playing a quick preview — pick “My Voice” as a narrator.");
              previewVoiceToken(`personal:${pv.voice_id}`);  // best-effort autoplay of the freshly cloned voice
            } else {
              toast.success("Your voice is ready — pick “My Voice” as a narrator.");
            }
          }
        }
      } catch (e) { /* keep polling */ }
    };
    clearInterval(clonePollRef.current);
    clonePollRef.current = setInterval(tick, 3000);
  };
  const uploadVoiceSample = async (file) => {
    const scope = cloneScopeRef.current;
    setBusyKey("clonevoice", true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("origin", process.env.REACT_APP_BACKEND_URL || window.location.origin);
      const url = scope === "project" ? `/projects/${id}/personal-voice` : "/voices/personal";
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.info("Cloning your voice… this runs in the background (up to ~2 min).");
      pollCloneStatus(scope);
    } catch (err) {
      setBusyKey("clonevoice", false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Voice cloning failed.");
    }
  };
  const onCloneVoiceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) await uploadVoiceSample(file);
  };
  const startCloneVoice = (scope) => {
    if (!voiceConsent) return toast.error("Please confirm this is your own voice / you have consent.");
    cloneScopeRef.current = scope;
    voiceFileRef.current?.click();
  };
  const startRecording = async (scope) => {
    if (!voiceConsent) return toast.error("Please confirm this is your own voice / you have consent.");
    cloneScopeRef.current = scope;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      recChunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size) recChunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        clearInterval(recTimerRef.current);
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recChunksRef.current, { type: mr.mimeType || "audio/webm" });
        setRecording(false);
        const ext = (mr.mimeType || "audio/webm").includes("ogg") ? "ogg" : "webm";
        await uploadVoiceSample(new File([blob], `myvoice.${ext}`, { type: blob.type }));
      };
      mediaRecRef.current = mr;
      setRecSeconds(0);
      mr.start();
      setRecording(true);
      recTimerRef.current = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    } catch (err) {
      toast.error("Microphone access was blocked. Allow mic access or upload a file instead.");
    }
  };
  const stopRecording = () => { try { mediaRecRef.current?.stop(); } catch {} };
  const removePersonalVoice = async (scope) => {
    setBusyKey("clonevoice", true);
    try {
      await api.delete(scope === "project" ? `/projects/${id}/personal-voice` : "/voices/personal");
      toast.success("Personal voice removed.");
      await reloadVoices();
      await load();
    } catch (err) {
      toast.error("Could not remove personal voice.");
    } finally { setBusyKey("clonevoice", false); }
  };

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const body = ttsEngine === "elevenlabs" ? { engine: "elevenlabs", voice_id: elVoice } : { engine: "openai", voice };
      const { data } = await api.post("/voices/preview", body);
      const audio = new Audio(fileUrl(data.storage_path));
      audio.play();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not preview voice.");
    } finally {
      setPreviewing(false);
    }
  };
  const [musicVol, setMusicVol] = useState(0.18);
  const musicRef = useRef(null);
  const [musicUploading, setMusicUploading] = useState(false);
  const [schedForm, setSchedForm] = useState({ platform: "tiktok", scheduled_at: "", caption: "", repeat: "none", repeat_count: 4 });
  const [duration, setDuration] = useState(0);
  const [libraryTracks, setLibraryTracks] = useState([]);
  const [libVol, setLibVol] = useState(0.18);
  const [commentText, setCommentText] = useState("");
  const [pinTime, setPinTime] = useState(false);
  const [members, setMembers] = useState([]);
  const [editScript, setEditScript] = useState(null); // null = view mode; array = editing
  const [scriptMeta, setScriptMeta] = useState({ total_duration: "", tone: "" });
  const [karaoke, setKaraoke] = useState(false);
  const [capPos, setCapPos] = useState("bottom");
  const [capSize, setCapSize] = useState("medium");
  const [capFontPx, setCapFontPx] = useState(20);
  const [capFont, setCapFont] = useState("sans");
  const [capOutline, setCapOutline] = useState("medium");
  const [capHlStyle, setCapHlStyle] = useState("text");
  const [showSafe, setShowSafe] = useState(false);
  const [previewSeg, setPreviewSeg] = useState(0);
  const [previewAspect, setPreviewAspect] = useState("9:16");
  const [playing, setPlaying] = useState(false);
  const [previewDims, setPreviewDims] = useState({ w: 0, h: 0 });
  const [kIdx, setKIdx] = useState(0);
  const [capMaxW, setCapMaxW] = useState(0);
  const [presets, setPresets] = useState([]);
  const [fontOpts, setFontOpts] = useState([]);
  const [presetSel, setPresetSel] = useState("");
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const previewBoxRef = useRef(null);
  const previewVideoRef = useRef(null);
  const [hlColor, setHlColor] = useState("#00E5FF");
  const [moodDraft, setMoodDraft] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [tplSel, setTplSel] = useState("");
  const [tplStart, setTplStart] = useState("");
  const [schedView, setSchedView] = useState("list");
  const [calDay, setCalDay] = useState(new Date());
  const videoRef = useRef(null);
  const [trim, setTrim] = useState([0, 0]);
  const [steps, setSteps] = useState(DEFAULT_STEPS);
  const [renderSubSize, setRenderSubSize] = useState("small");
  const [renderVoice, setRenderVoice] = useState("nova");
  const [renderMotion, setRenderMotion] = useState("standard");
  const [sceneMode, setSceneMode] = useState("balanced");
  const [sceneCount, setSceneCount] = useState(5);
  const [musicBed, setMusicBed] = useState("calm");
  const [filmMusicVol, setFilmMusicVol] = useState(0.14);
  const [filmTransition, setFilmTransition] = useState("crossfade");
  const [aiMotion, setAiMotion] = useState(false);
  const [motionTier, setMotionTier] = useState("kling_v2_1_standard");
  const [lipSync, setLipSync] = useState(true);
  const [visualStyle, setVisualStyle] = useState("animated");
  const [storyStyle, setStoryStyle] = useState("cinematic");
  const [dialogueLength, setDialogueLength] = useState("balanced");
  const [splitScene, setSplitScene] = useState(null);
  const [splitText, setSplitText] = useState("");
  const [charVoices, setCharVoices] = useState({});
  const [showCharVoices, setShowCharVoices] = useState(false);
  const [editingScene, setEditingScene] = useState(null);
  const [editText, setEditText] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);
  const previewAudioRef = useRef(null);
  const pollRef = useRef(null);
  const renderPollRef = useRef(null);
  const resizePollRef = useRef(null);

  const load = async (opts = {}) => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      if (data.assets?.length && !composeAsset) setComposeAsset(data.assets[0].id);
      if (data.assets?.length && !thumbAsset) setThumbAsset(data.assets[0].id);
      return data;
    } catch (err) {
      const status = err.response?.status;
      if (status === 404 || status === 403) {
        // genuinely gone / not yours -> back to dashboard
        toast.error("Project not found");
        navigate("/dashboard");
      } else if (!opts.silent) {
        // transient network / timeout / 5xx -> keep the current view, don't eject
        toast.error("Couldn't reach the server — please try again");
      }
      return null;
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const filmSyncedRef = useRef(false);
  useEffect(() => {
    const f = project?.film;
    if (f?.scenes?.length && !filmSyncedRef.current) {
      filmSyncedRef.current = true;
      if (typeof f.ai_motion === "boolean") setAiMotion(f.ai_motion);
      if (f.motion_tier) setMotionTier(f.motion_tier);
      if (typeof f.lip_sync === "boolean") setLipSync(f.lip_sync);
      if (f.visual_style) setVisualStyle(f.visual_style);
      if (f.story_style) setStoryStyle(f.story_style);
      if (f.dialogue_length) setDialogueLength(f.dialogue_length);
    }
  }, [project?.film?.scenes?.length]);
  useEffect(() => {
    api.get("/music-library").then(({ data }) => setLibraryTracks(data.tracks || [])).catch(() => {});
    api.get(`/projects/${id}/members`).then(({ data }) => setMembers(data.members || [])).catch(() => {});
    api.get("/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
    api.get("/caption-presets").then(({ data }) => { setPresets(data.presets || []); setFontOpts(data.fonts || []); }).catch(() => {});
    api.get("/credit-costs").then(({ data }) => setCosts(data.costs || {})).catch(() => {});
  }, [id]);

  const loadPresets = () => api.get("/caption-presets").then(({ data }) => setPresets(data.presets || [])).catch(() => {});
  const applyPreset = (pid) => {
    setPresetSel(pid);
    const p = presets.find((x) => x.id === pid);
    if (!p) return;
    setCapPos(p.position || "bottom");
    setCapSize(p.size || "medium");
    setCapFont(p.font || "sans");
    setCapOutline(p.outline || "medium");
    setCapHlStyle(p.highlight_style || "text");
    if (p.highlight_color) { setHlColor(p.highlight_color); if (!karaoke) { setKaraoke(true); toast.message("Karaoke enabled by preset"); } }
    toast.success(`Applied "${p.name}"`);
  };
  const savePreset = async () => {
    if (!presetName.trim()) return toast.error("Name your preset");
    setSavingPreset(true);
    try {
      const { data } = await api.post("/caption-presets", {
        name: presetName.trim(), position: capPos, size: capSize, font: capFont, outline: capOutline,
        highlight_style: capHlStyle, highlight_color: karaoke ? hlColor : undefined,
      });
      setPresets((prev) => [data.preset, ...prev]);
      setPresetName("");
      setPresetSel(data.preset.id);
      toast.success("Preset saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingPreset(false); }
  };
  const deletePreset = async (pid) => {
    try {
      await api.delete(`/caption-presets/${pid}`);
      setPresets((prev) => prev.filter((x) => x.id !== pid));
      if (presetSel === pid) setPresetSel("");
      toast.success("Preset deleted");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const loadTemplates = () => api.get("/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});

  useEffect(() => {
    const box = previewBoxRef.current;
    if (!box) return;
    const recompute = () => {
      if (!box) return;
      const bw = box.clientWidth, bh = box.clientHeight;
      if (!bw || !bh) return;
      // Frame = the chosen-aspect preview box; captions scale to frame height (matches output)
      setCapFontPx(Math.max(bh * 0.05 * (CAP_SCALE[capSize] || 1), 8));
      setCapMaxW(Math.round(bw * 0.9));
      setPreviewDims({ w: Math.round(bw), h: Math.round(bh) });
    };
    recompute();
    const raf = requestAnimationFrame(recompute);
    const timers = [setTimeout(recompute, 120), setTimeout(recompute, 400), setTimeout(recompute, 900)];
    const ro = new ResizeObserver(recompute);
    ro.observe(box);
    const vid = previewVideoRef.current;
    const onMeta = () => { recompute(); try { if (vid.paused && vid.currentTime < 0.05) vid.currentTime = 0.1; } catch (e) {} };
    if (vid) { vid.addEventListener("loadedmetadata", onMeta); vid.addEventListener("seeked", recompute); }
    return () => { cancelAnimationFrame(raf); timers.forEach(clearTimeout); ro.disconnect(); if (vid) { vid.removeEventListener("loadedmetadata", onMeta); vid.removeEventListener("seeked", recompute); } };
  }, [capSize, tab, project, showSafe, previewAspect]);

  useEffect(() => {
    if (!playing || tab !== "voiceover") return;
    const segs = project?.script?.segments || [];
    if (!segs.length) { setPlaying(false); return; }
    const idx = Math.min(previewSeg, segs.length - 1);
    const seg = segs[idx];
    const dur = Math.max(parseTs(seg.end) - parseTs(seg.start), 1.2);
    const vid = previewVideoRef.current;
    if (vid) { try { vid.currentTime = parseTs(seg.start); const p = vid.play(); if (p) p.catch(() => {}); } catch (e) {} }
    const t = setTimeout(() => setPreviewSeg((prev) => (prev + 1) % segs.length), dur * 1000);
    return () => clearTimeout(t);
  }, [playing, previewSeg, tab, project]);

  useEffect(() => {
    if (!playing) { const vid = previewVideoRef.current; if (vid) { try { vid.pause(); } catch (e) {} } }
  }, [playing]);

  useEffect(() => {
    if (!karaoke || tab !== "voiceover") { setKIdx(0); return; }
    const txt = ((project?.script?.segments || [])[previewSeg]?.narration) || "Your captions appear here";
    const n = txt.split(/\s+/).filter(Boolean).length || 1;
    const t = setInterval(() => setKIdx((p) => (p + 1) % (n + 3)), 320);
    return () => clearInterval(t);
  }, [karaoke, tab, project, previewSeg]);

  const saveTemplate = async () => {
    if (!(project.schedule?.length)) return toast.error("Add some scheduled posts first");
    if (!tplName.trim()) return toast.error("Name your template");
    const days = project.schedule.map((s) => new Date(s.scheduled_at));
    const base = new Date(Math.min(...days.map((d) => d.getTime())));
    const baseDay = new Date(base.getFullYear(), base.getMonth(), base.getDate()).getTime();
    const items = project.schedule.map((s) => {
      const d = new Date(s.scheduled_at);
      const day0 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      return {
        platform: s.platform,
        day_offset: Math.round((day0 - baseDay) / 86400000),
        time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
        caption: s.caption || "",
      };
    });
    try {
      await api.post("/templates", { name: tplName, items });
      toast.success("Template saved");
      setTplName("");
      loadTemplates();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const applyTemplate = async () => {
    if (!tplSel) return toast.error("Pick a template");
    if (!tplStart) return toast.error("Pick a start date");
    setBusyKey("applytpl", true);
    try {
      await api.post(`/projects/${id}/apply-template`, { template_id: tplSel, start_date: new Date(tplStart).toISOString() });
      toast.success("Template applied to schedule");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusyKey("applytpl", false); }
  };

  const delTemplate = async (tid) => {
    try { await api.delete(`/templates/${tid}`); loadTemplates(); } catch { toast.error("Could not delete"); }
  };

  // poll while the one-click pipeline runs
  useEffect(() => {
    const running = project?.pipeline_status?.running;
    if (running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const d = await load({ silent: true });
        if (!d) return;  // transient error — keep polling, don't eject
        if (!d.pipeline_status?.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success("One-click pipeline finished!");
        }
      }, 3000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line
  }, [project?.pipeline_status?.running]);

  // poll while a background render runs
  useEffect(() => {
    const running = project?.render_status?.running;
    if (running && !renderPollRef.current) {
      renderPollRef.current = setInterval(async () => {
        const d = await load({ silent: true });
        if (!d) return;  // transient error — keep polling, don't eject
        if (!d.render_status?.running) {
          clearInterval(renderPollRef.current);
          renderPollRef.current = null;
          // completion toast + browser notification handled app-wide by RenderNotifier
          if (!d?.render_status?.error && d?.final_render) {
            setReadyBanner(true);
          }
          refreshUser();
        }
      }, 3000);
    }
    return () => { if (renderPollRef.current) { clearInterval(renderPollRef.current); renderPollRef.current = null; } };
    // eslint-disable-next-line
  }, [project?.render_status?.running]);

  useEffect(() => {
    const running = project?.resize_status?.running;
    if (running && !resizePollRef.current) {
      resizePollRef.current = setInterval(async () => {
        const d = await load({ silent: true });
        if (!d) return;  // transient error — keep polling
        if (!d.resize_status?.running) {
          clearInterval(resizePollRef.current);
          resizePollRef.current = null;
          setBusy((b) => {
            const nb = { ...b };
            Object.keys(nb).forEach((k) => { if (k.startsWith("resize")) nb[k] = false; });
            return nb;
          });
          if (d.resize_status?.error) toast.error(d.resize_status.error);
          else toast.success("Reframe ready!");
          refreshUser();
        }
      }, 3000);
    }
    return () => { if (resizePollRef.current) { clearInterval(resizePollRef.current); resizePollRef.current = null; } };
    // eslint-disable-next-line
  }, [project?.resize_status?.running]);

  const setBusyKey = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  // Central API error handler: 402 = out of credits / tier-locked -> upgrade prompt
  const handleApiError = (err, fallback) => {
    const status = err.response?.status;
    const msg = formatApiErrorDetail(err.response?.data?.detail) || fallback || "Something went wrong";
    if (status === 402) {
      toast.error(msg, {
        duration: 7000,
        action: { label: "Upgrade / Buy credits", onClick: () => navigate("/pricing") },
      });
    } else {
      toast.error(msg);
    }
    refreshUser();  // credits may have been refunded on a failed job
  };

  // --- Credit cost hints + low-credit nudge ---
  const owner = !!(authUser && authUser.is_owner);
  const costFor = (endpoint) => (costs ? costs[ENDPOINT_COST[endpoint]] ?? 0 : 0);
  const credits = authUser?.credits ?? 0;
  const canAfford = (endpoint) => {
    if (owner) return true;  // owner is billing-exempt
    const cost = costFor(endpoint);
    if (cost > 0 && credits < cost) {
      toast.error(`You need ${cost} credits, you have ${credits}.`, {
        duration: 7000,
        action: { label: "Get credits", onClick: () => navigate("/pricing") },
      });
      return false;
    }
    return true;
  };
  const nudgeIfLow = (data) => {
    if (owner) return;  // no nags for owner
    const bal = data?.credits;
    if (typeof bal === "number" && bal > 0 && bal < LOW_CREDIT_THRESHOLD) {
      toast(`Running low — ${bal} credits left`, {
        description: "Top up anytime to keep creating.",
        action: { label: "Top up", onClick: () => navigate("/pricing") },
      });
    }
  };
  const CostHint = ({ endpoint }) => {
    if (owner) return null;  // owner is unlimited — hide cost hints
    const cost = costFor(endpoint);
    if (!cost) return null;
    return (
      <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/10 text-white/60 align-middle"
        data-testid={`cost-hint-${ENDPOINT_COST[endpoint]}`}>{cost} cr</span>
    );
  };

  const changeEngine = async (v) => {
    try {
      await api.patch(`/projects/${id}`, { provider: v });
      setProject((p) => ({ ...p, provider: v }));
      toast.success(`Engine set to ${ENGINES.find((e) => e.value === v)?.label}`);
    } catch {
      toast.error("Could not change engine");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    await doUpload(file);
  };

  const doUpload = async (file) => {
    // client-side safeguards for instant, friendly feedback
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const isVideo = file.type.startsWith("video/") || VIDEO_EXTS.includes(ext);
    if (!isVideo) {
      toast.error("Unsupported file type. Please upload a video (MP4, MOV, WEBM or MKV).");
      return;
    }
    const mb = file.size / (1024 * 1024);
    if (mb > MAX_VIDEO_MB) {
      toast.error(`That video is ${mb.toFixed(0)}MB — the limit is ${MAX_VIDEO_MB}MB. Please upload a smaller file.`);
      return;
    }
    lastFileRef.current = file;
    setUploadError("");
    setUploading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${id}/upload-video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => { if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100)); },
      });
      toast.success("Video uploaded");
      lastFileRef.current = null;
      await load();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Upload failed — please try again.";
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const retryUpload = () => { if (lastFileRef.current) doUpload(lastFileRef.current); };

  const run = async (key, endpoint, okMsg, goTab, body) => {
    if (!canAfford(endpoint)) return;
    setBusyKey(key, true);
    try {
      await api.post(`/projects/${id}/${endpoint}`, body || {});
      toast.success(okMsg);
      await load();
      const u = await refreshUser();
      nudgeIfLow(u);
      if (goTab) setTab(goTab);
    } catch (err) {
      handleApiError(err, "Something went wrong");
    } finally {
      setBusyKey(key, false);
    }
  };

  const doRegen = async () => {
    if (!regen?.prompt?.trim()) return;
    setBusyKey("regen", true);
    try {
      await api.post(`/projects/${id}/regenerate-graphic`, { asset_id: regen.id, prompt: regen.prompt });
      toast.success("Graphic regenerated");
      setRegen(null);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Regeneration failed");
    } finally {
      setBusyKey("regen", false);
    }
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  const uploadMusic = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMusicUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("volume", String(musicVol));
      await api.post(`/projects/${id}/music-bed`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Music mixed under voiceover");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Mixing failed");
    } finally { setMusicUploading(false); }
  };

  const doResize = async (ratio) => {
    if (!canAfford("resize")) return;
    setBusyKey(`resize_${ratio}`, true);
    try {
      await api.post(`/projects/${id}/resize`, { ratio });
      toast.info(`Reframing to ${ratio}…`);
      await load();   // picks up resize_status.running -> polling effect finishes it
    } catch (err) {
      handleApiError(err, "Reframe failed");
      setBusyKey(`resize_${ratio}`, false);
    }
  };

  const reframeAll = async () => {
    if (!canAfford("reframe-all")) return;
    setBusyKey("resize_all", true);
    try {
      await api.post(`/projects/${id}/reframe-all`);
      toast.info("Exporting all 3 aspects…");
      await load();   // polling effect handles completion
    } catch (err) {
      handleApiError(err, "Reframe failed");
      setBusyKey("resize_all", false);
    }
  };

  const captureCover = async () => {
    if (!hasVideo) return;
    if (!canAfford("cover-frame")) return;
    setBusyKey("cover", true);
    try {
      const { data } = await api.post(`/projects/${id}/cover-frame`, {
        aspect: previewAspect, segment_index: previewSeg,
        karaoke, position: capPos, size: capSize, font: capFont,
        outline: capOutline, highlight_style: capHlStyle,
        highlight_color: karaoke ? hlColor : undefined,
      });
      const a = document.createElement("a");
      a.href = fileUrl(data.cover.storage_path);
      a.download = `cover-${previewAspect.replace(":", "x")}-line${previewSeg + 1}.png`;
      a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Cover frame downloaded");
      nudgeIfLow(await refreshUser());
    } catch (err) {
      handleApiError(err, "Cover capture failed");
    } finally { setBusyKey("cover", false); }
  };

  const addSchedule = async () => {
    if (!schedForm.scheduled_at) return toast.error("Pick a date & time");
    setBusyKey("sched", true);
    try {
      await api.post(`/projects/${id}/schedule`, schedForm);
      toast.success("Post scheduled");
      setSchedForm({ platform: "tiktok", scheduled_at: "", caption: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusyKey("sched", false); }
  };

  const delSchedule = async (sid) => {
    try { await api.delete(`/projects/${id}/schedule/${sid}`); await load(); }
    catch { toast.error("Could not remove"); }
  };

  const applyLibraryBed = (trackId) =>
    run(`lib_${trackId}`, "music-bed-library", "Library track mixed in!", "voiceover", { track_id: trackId, volume: libVol });

  const addComment = async () => {
    if (!commentText.trim()) return;
    setBusyKey("comment", true);
    try {
      const body = { text: commentText };
      if (pinTime && videoRef.current) body.at_seconds = Math.round(videoRef.current.currentTime * 10) / 10;
      await api.post(`/projects/${id}/comments`, body);
      setCommentText("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusyKey("comment", false); }
  };

  const delComment = async (cid) => {
    try { await api.delete(`/projects/${id}/comments/${cid}`); await load(); }
    catch { toast.error("Could not delete"); }
  };

  const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  const transcribe = () => run("transcribe", "transcribe", "Captions transcribed from audio!", "voiceover");

  const autoPickBed = () => run("autobed", "music-bed-auto", "Bed auto-matched to your mood!", "voiceover");

  const saveMood = async (m) => {
    setMoodDraft(m);
    try {
      await api.post(`/projects/${id}/mood`, { mood: m });
      setProject((p) => ({ ...p, analysis: { ...(p.analysis || {}), mood: m } }));
    } catch { /* ignore */ }
  };

  const startEditScript = () => {
    setScriptMeta({ total_duration: project.script?.total_duration || "", tone: project.script?.tone || "" });
    setEditScript((project.script?.segments || []).map((s) => ({ ...s })));
  };
  const updateSeg = (i, patch) => setEditScript((segs) => segs.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addSeg = () => setEditScript((segs) => [...segs, { beat: "New", start: "0:00", end: "0:05", narration: "" }]);
  const removeSeg = (i) => setEditScript((segs) => segs.filter((_, idx) => idx !== i));
  const saveScript = async () => {
    setBusyKey("savescript", true);
    try {
      await api.put(`/projects/${id}/script`, { ...scriptMeta, segments: editScript });
      toast.success("Captions saved");
      setEditScript(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusyKey("savescript", false); }
  };

  const startOneClick = async () => {
    if (!canAfford("run-all")) return;
    try {
      await api.post(`/projects/${id}/run-all`);
      toast.info("One-click pipeline started…");
      await load();
      nudgeIfLow(await refreshUser());
    } catch (err) {
      handleApiError(err, "Could not start");
    }
  };

  const moveStep = (i, dir) => {
    setSteps((s) => {
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const copy = [...s];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };
  const updateStep = (i, patch) => setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, ...patch } : st)));

  const generateFilm = async () => {
    if (!canAfford("film")) return;
    setReadyBanner(false);
    setBusyKey("film", true);
    try {
      await api.post(`/projects/${id}/generate-film`, {
        voiceover_voice: renderVoice, motion_strength: renderMotion, subtitle_size: renderSubSize,
        scene_mode: sceneMode, scene_count: sceneCount, music_bed: musicBed, music_volume: filmMusicVol,
        transition: filmTransition, ai_motion: aiMotion, motion_tier: motionTier, lip_sync: lipSync, visual_style: visualStyle, story_style: storyStyle, dialogue_length: dialogueLength,
        origin: process.env.REACT_APP_BACKEND_URL,
      });
      toast.info(aiMotion
        ? "Generating your AI-motion cinematic film… real motion takes several minutes."
        : "Generating your AI cinematic film… this takes a couple of minutes.");
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Film generation failed");
    } finally {
      setBusyKey("film", false);
    }
  };

  const uploadCharRef = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusyKey("charref", true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${id}/character-ref`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Character reference locked — new films will match this look.");
      await load();
    } catch (err) {
      handleApiError(err, "Upload failed");
    } finally {
      setBusyKey("charref", false);
      e.target.value = "";
    }
  };

  const removeCharRef = async () => {
    setBusyKey("charref", true);
    try {
      await api.delete(`/projects/${id}/character-ref`);
      await load();
    } catch (err) {
      handleApiError(err, "Remove failed");
    } finally {
      setBusyKey("charref", false);
    }
  };

  const uploadCharacterFace = async (name, file) => {
    if (!file) return;
    setBusyKey(`face-${name}`, true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${id}/character-ref/${encodeURIComponent(name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${name}'s face locked — reshoot or re-render their scenes to apply.`);
      await load();
    } catch (err) {
      handleApiError(err, "Upload failed");
    } finally {
      setBusyKey(`face-${name}`, false);
    }
  };

  const removeCharacterFace = async (name) => {
    setBusyKey(`face-${name}`, true);
    try {
      await api.delete(`/projects/${id}/character-ref/${encodeURIComponent(name)}`);
      await load();
    } catch (err) {
      handleApiError(err, "Remove failed");
    } finally {
      setBusyKey(`face-${name}`, false);
    }
  };

  const regenerateScene = async (index) => {
    setBusyKey(`scene-${index}`, true);
    try {
      await api.post(`/projects/${id}/regenerate-scene`, { index });
      toast.success(`Scene ${index + 1} reshot. Re-render the film to see it in the video.`);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Scene regeneration failed");
    } finally {
      setBusyKey(`scene-${index}`, false);
    }
  };

  const reanimateScene = async (index) => {
    if (!canAfford("render")) return;
    setBusyKey(`reanim-${index}`, true);
    try {
      await api.post(`/projects/${id}/reanimate-scene`, { index, origin: process.env.REACT_APP_BACKEND_URL });
      toast.info(`Reanimating scene ${index + 1} with real AI motion… this takes a minute or two.`);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Reanimate failed");
    } finally {
      setBusyKey(`reanim-${index}`, false);
    }
  };

  const reassembleFilm = async () => {
    if (!canAfford("render")) return;
    setBusyKey("film", true);
    try {
      await api.post(`/projects/${id}/reassemble-film`, { music_bed: musicBed, music_volume: filmMusicVol, transition: filmTransition, ai_motion: aiMotion, motion_tier: motionTier, lip_sync: lipSync, visual_style: visualStyle, story_style: storyStyle, dialogue_length: dialogueLength, origin: process.env.REACT_APP_BACKEND_URL });
      toast.info(aiMotion ? "Re-rendering with real AI motion… this takes several minutes." : "Re-rendering your film with the updated scenes…");
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Re-render failed");
    } finally {
      setBusyKey("film", false);
    }
  };

  const revoiceFilm = async (voice) => {
    setBusyKey("revoice", true);
    try {
      const { data } = await api.post(`/projects/${id}/revoice-film`, { voice });
      toast.success(`Whole film re-voiced (${data.revoiced} scene${data.revoiced === 1 ? "" : "s"}${data.locked_skipped ? `, ${data.locked_skipped} locked skipped` : ""}). Re-render to apply.`);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Re-voice failed");
    } finally {
      setBusyKey("revoice", false);
    }
  };

  useEffect(() => { if (project?.film?.characters) setCharVoices(project.film.characters); }, [project?.film?.characters]);
  const distinctCharacters = () => [...new Set((project?.film_scenes || []).map((s) => s.character || "Narrator"))];
  const saveCharacterVoices = async () => {
    setBusyKey("charvoices", true);
    try {
      await api.post(`/projects/${id}/character-voices`, { mapping: charVoices });
      toast.success("Character voices saved — re-render to hear them.");
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Could not save character voices");
    } finally { setBusyKey("charvoices", false); }
  };

  const recastVoices = async () => {
    setBusyKey("recast", true);
    try {
      const { data } = await api.post(`/projects/${id}/recast-voices`, {});
      toast.info(`Recasting voices for ${data.scenes_to_revoice} scene(s)… runs in the background.`);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Could not recast voices");
    } finally { setBusyKey("recast", false); }
  };

  const restoreVoices = async () => {
    setBusyKey("restore", true);
    try {
      await api.post(`/projects/${id}/restore-voices`, {});
      toast.success("Previous character voices restored.");
      await load();
    } catch (err) {
      handleApiError(err, "Could not restore voices");
    } finally { setBusyKey("restore", false); }
  };


  const toggleSceneLock = async (index, locked) => {
    setBusyKey(`lock-${index}`, true);
    try {
      await api.post(`/projects/${id}/scene-lock`, { index, locked });
      await load();
    } catch (err) {
      handleApiError(err, "Lock failed");
    } finally {
      setBusyKey(`lock-${index}`, false);
    }
  };

  const toggleSceneLipSync = async (index, disabled) => {
    setBusyKey(`ls-${index}`, true);
    try {
      await api.post(`/projects/${id}/scene-lip-sync`, { index, disabled });
      await load();
      toast.success(disabled ? "Lip sync off for this scene — re-render to apply." : "Lip sync on for this scene — re-render to apply.");
    } catch (err) {
      handleApiError(err, "Update failed");
    } finally {
      setBusyKey(`ls-${index}`, false);
    }
  };

  const setSceneStyle = async (index, style) => {
    setBusyKey(`style-${index}`, true);
    try {
      await api.post(`/projects/${id}/scene-style`, { index, style });
      toast.success(`Scene ${index + 1} restyled to ${style === "photoreal" ? "Photoreal" : "Animated"}. Re-render to apply.`);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Restyle failed");
    } finally {
      setBusyKey(`style-${index}`, false);
    }
  };

  const submitSplitScene = async () => {
    const lines = splitText.split("\n").map((raw) => {
      const t = raw.trim();
      if (!t) return null;
      const m = t.match(/^([^:]{1,40}):\s*(.+)$/);
      return m ? { character: m[1].trim(), narration: m[2].trim() } : { character: "Narrator", narration: t };
    }).filter(Boolean);
    if (lines.length < 2) { toast.error("Add at least two lines like 'Amara: Hello'"); return; }
    setBusyKey("split", true);
    try {
      await api.post(`/projects/${id}/scene-split`, { index: splitScene, lines });
      toast.info("Splitting scene into per-speaker shots… this runs in the background.");
      setSplitScene(null); setSplitText("");
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Split failed");
    } finally {
      setBusyKey("split", false);
    }
  };

  const playSceneVoice = (audioPath) => {
    if (!audioPath) { toast.error("No audio for this scene yet."); return; }
    try {
      if (auditionAudioRef.current) { auditionAudioRef.current.pause(); }
      auditionAudioRef.current = new Audio(fileUrl(audioPath));
      auditionAudioRef.current.play();
    } catch (e) { /* noop */ }
  };

  const saveSceneNarration = async (index, reshoot) => {
    if (!editText.trim()) return;
    setBusyKey(`narr-${index}`, true);
    try {
      await api.post(`/projects/${id}/scene-narration`, { index, narration: editText.trim(), reshoot });
      toast.success(`Scene ${index + 1} re-voiced. Re-render to hear it in the film.`);
      setEditingScene(null);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Edit failed");
    } finally {
      setBusyKey(`narr-${index}`, false);
    }
  };

  const onSceneDrop = async (fromIdx, toIdx) => {
    setDragIndex(null);
    if (fromIdx === null || fromIdx === toIdx) return;
    const scenes = [...(project.film_scenes || [])].sort((a, b) => a.index - b.index);
    const [moved] = scenes.splice(fromIdx, 1);
    scenes.splice(toIdx, 0, moved);
    try {
      await api.post(`/projects/${id}/reorder-scenes`, { order: scenes.map((s) => s.id) });
      await load();
    } catch (err) {
      handleApiError(err, "Reorder failed");
    }
  };

  const nudgeSceneDuration = async (index, current, delta) => {
    const dur = Math.max(1.5, Math.min(20, Math.round(((current || 4) + delta) * 2) / 2));
    setBusyKey(`dur-${index}`, true);
    try {
      await api.post(`/projects/${id}/scene-duration`, { index, duration: dur });
      await load();
    } catch (err) {
      handleApiError(err, "Duration change failed");
    } finally {
      setBusyKey(`dur-${index}`, false);
    }
  };

  const addScene = async (afterIndex) => {
    if (!canAfford("graphics")) return;
    setBusyKey("addscene", true);
    try {
      await api.post(`/projects/${id}/add-scene`, { after_index: afterIndex });
      toast.success("New scene added. Re-render to include it in the film.");
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Add scene failed");
    } finally {
      setBusyKey("addscene", false);
    }
  };

  const deleteScene = async (index) => {
    setBusyKey(`del-${index}`, true);
    try {
      await api.post(`/projects/${id}/delete-scene`, { index });
      toast.success("Scene removed.");
      await load();
    } catch (err) {
      handleApiError(err, "Delete failed");
    } finally {
      setBusyKey(`del-${index}`, false);
    }
  };

  const setSceneVoice = async (index, voice) => {
    setBusyKey(`voice-${index}`, true);
    try {
      await api.post(`/projects/${id}/scene-voice`, { index, voice });
      toast.success(`Scene ${index + 1} re-voiced. Re-render to apply.`);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Voice change failed");
    } finally {
      setBusyKey(`voice-${index}`, false);
    }
  };

  const orderedScenes = () => [...(project?.film_scenes || [])].sort((a, b) => a.index - b.index);

  const voiceLabel = (token) => {
    if (!token) return "Film voice";
    const hit = buildVoiceItems(voiceCatalog, project).find((o) => o.value === token);
    return hit ? hit.label : token;
  };

  // Detect "Name: line" dialogue inside a scene's narration → suggested per-speaker lines.
  const detectDialogue = (text) => {
    if (!text) return [];
    const marker = /(^|\s)([A-Z][A-Za-z'’.\-]{1,20}(?:\s[A-Z][A-Za-z'’.\-]{1,20})?):\s/g;
    const hits = [];
    let m;
    while ((m = marker.exec(text)) !== null) {
      hits.push({ start: m.index + m[1].length, textStart: marker.lastIndex, name: m[2].trim() });
    }
    if (hits.length < 2) return [];
    const lines = [];
    for (let i = 0; i < hits.length; i++) {
      const to = i + 1 < hits.length ? hits[i + 1].start : text.length;
      const line = text.slice(hits[i].textStart, to).trim().replace(/^["“”']+|["“”']+$/g, "").trim();
      if (line) lines.push({ character: hits[i].name, narration: line });
    }
    return lines.length >= 2 ? lines : [];
  };

  const autoSplitScene = async (index, lines) => {
    setBusyKey("split", true);
    try {
      await api.post(`/projects/${id}/scene-split`, { index, lines });
      toast.info(`Splitting ${lines.length} detected lines into per-speaker shots… runs in the background.`);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Auto-split failed");
    } finally {
      setBusyKey("split", false);
    }
  };

  const dialogueSceneCount = () => (project?.film_scenes || []).filter((s) => !s.locked && detectDialogue(s.narration).length >= 2).length;

  const autoSplitAll = async () => {
    setBusyKey("splitall", true);
    try {
      const { data } = await api.post(`/projects/${id}/auto-split-all`, {});
      toast.info(`Splitting ${data.scenes_to_split} dialogue scene(s) into ${data.new_shots} per-speaker shots… runs in the background.`);
      await load();
      refreshUser();
    } catch (err) {
      handleApiError(err, "Auto-split failed");
    } finally {
      setBusyKey("splitall", false);
    }
  };

  const startPreview = () => { setPreviewIdx(0); };
  const advancePreview = () => {
    const scenes = orderedScenes();
    setPreviewIdx((i) => (i === null || i + 1 >= scenes.length ? null : i + 1));
  };

  if (!project)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[#00E5FF] border-t-transparent animate-spin" />
      </div>
    );

  const hasVideo = !!project.video;
  const a = project.analysis;
  const t = project.transform;
  const assets = project.assets || [];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to studio
          </button>
          <ShareDialog projectId={id} />
        </div>

        {readyBanner && project.final_render && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-[#39FF14]/40 bg-[#39FF14]/10 px-4 py-3 float-up" data-testid="ready-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#39FF14] shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white">Your video is ready</p>
                <p className="text-xs text-white/60">Preview & download it below — you can keep editing anytime.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => document.querySelector("[data-testid='final-result']")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="h-8 rounded-full bg-[#39FF14] text-black hover:bg-[#39FF14]/90 text-xs" data-testid="ready-banner-view">View video</Button>
              <button onClick={() => setReadyBanner(false)} className="text-white/40 hover:text-white" data-testid="ready-banner-dismiss"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* LEFT: pipeline */}
          <aside className="space-y-3" data-testid="pipeline-sidebar">
            <div className="bg-zinc-950 border border-white/10 rounded-lg p-5">
              <h1 className="font-display text-xl font-semibold tracking-tight truncate">{project.title}</h1>
              <p className="text-sm text-white/50 font-light mt-1 line-clamp-2">{project.description || "No description"}</p>
              <Badge variant="outline" className="mt-3 border-[#00E5FF]/40 text-[#00E5FF] capitalize">{project.status}</Badge>
            </div>

            {/* engine selector */}
            <div className="bg-zinc-950 border border-white/10 rounded-lg p-4" data-testid="engine-card">
              <div className="flex items-center gap-2 mb-2">
                <Cpu className="w-4 h-4 text-[#39FF14]" />
                <span className="font-display text-sm tracking-tight">AI engine</span>
              </div>
              <Select value={project.provider || "openai"} onValueChange={changeEngine}>
                <SelectTrigger className="bg-black border-white/15 text-sm" data-testid="engine-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                  {ENGINES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-white/30 mt-2">Auto-fallback if the chosen engine fails.</p>
            </div>

            {/* one-click (video-centric shortcut) */}
            {hasVideo && (
            <div className="bg-zinc-950 border border-[#39FF14]/30 rounded-lg p-4" data-testid="oneclick-card">
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="w-4 h-4 text-[#39FF14]" />
                <span className="font-display text-sm tracking-tight">One-click studio</span>
              </div>
              <p className="text-xs text-white/40 mb-3">Run story, graphics, captions, script, music, hooks & thumbnails in one go.</p>
              <div className="flex items-end gap-2 mb-3" data-testid="oneclick-voice-row">
                <div className="flex-1 min-w-0">
                  <label className="text-[11px] text-white/40">Narrator voice</label>
                  {ttsEngine === "elevenlabs" ? (
                    <Select value={elVoice} onValueChange={setElVoice}>
                      <SelectTrigger className="bg-black border-white/15 mt-1 h-9 text-xs" data-testid="oneclick-el-voice"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-white/10 text-white">
                        {(voiceCatalog?.elevenlabs?.voices || []).map((v) => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={voice} onValueChange={setVoice}>
                      <SelectTrigger className="bg-black border-white/15 mt-1 h-9 text-xs capitalize" data-testid="oneclick-voice"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-white/10 text-white">
                        {VOICES.map((v) => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {voiceCatalog?.elevenlabs?.available && (
                  <Button variant="outline" size="sm" onClick={() => setTtsEngine(ttsEngine === "elevenlabs" ? "openai" : "elevenlabs")}
                    className="h-9 rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white text-[11px] px-3" data-testid="oneclick-engine-toggle">
                    {ttsEngine === "elevenlabs" ? "Premium" : "Standard"}
                  </Button>
                )}
                <Button variant="outline" size="icon" onClick={previewVoice} disabled={previewing}
                  className="h-9 w-9 rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white shrink-0" data-testid="oneclick-preview-btn" title="Preview voice">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                </Button>
              </div>
              {project.pipeline_status?.running ? (
                <div className="flex items-center gap-2 text-sm text-[#39FF14]" data-testid="pipeline-progress">
                  <Loader2 className="w-4 h-4 animate-spin" /> {project.pipeline_status.step}…
                </div>
              ) : (
                <Button onClick={startOneClick} disabled={!hasVideo}
                  className="w-full rounded-full bg-[#39FF14] text-black hover:bg-[#39FF14]/90 font-medium disabled:opacity-40" data-testid="oneclick-btn">
                  <Rocket className="w-4 h-4 mr-1" /> Run everything<CostHint endpoint="run-all" />
                </Button>
              )}
            </div>
            )}

            <PipelineStep n={1} icon={Upload} title="Upload video (optional)" done={hasVideo}>
              <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={onUpload} data-testid="video-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline"
                className="w-full mt-2 border-white/15 bg-transparent hover:bg-white/5 text-white" data-testid="upload-video-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading… {uploadPct}%</> : hasVideo ? "Replace video" : "Choose video"}
              </Button>
              {uploading && (
                <div className="mt-2" data-testid="upload-progress">
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#FF007F] transition-all duration-200"
                      style={{ width: `${uploadPct}%` }} data-testid="upload-progress-bar" />
                  </div>
                  <p className="text-[11px] text-white/40 mt-1">
                    {uploadPct < 100 ? `Uploading ${uploadPct}% — keep this tab open` : "Processing your video…"}
                  </p>
                </div>
              )}
              {!uploading && uploadError && (
                <div className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2" data-testid="upload-error">
                  <p className="text-[11px] text-red-300">{uploadError}</p>
                  {lastFileRef.current && (
                    <Button onClick={retryUpload} size="sm"
                      className="mt-2 h-7 rounded-full bg-red-500/90 hover:bg-red-500 text-white text-xs" data-testid="upload-retry-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />Retry upload
                    </Button>
                  )}
                </div>
              )}
            </PipelineStep>

            <PipelineStep n={2} icon={Wand2} title="AI story & 3D plan" done={!!a}>
              <Button onClick={() => run("analyze", "analyze", "Story enhanced!", "story")} disabled={busy.analyze}
                className="w-full mt-2 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium disabled:opacity-40" data-testid="analyze-btn">
                {busy.analyze ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Thinking…</> : a ? "Re-analyze" : "Enhance story"}<CostHint endpoint="analyze" />
              </Button>
            </PipelineStep>

            <PipelineStep n={3} icon={Sparkles} title="Generate graphics" done={assets.length > 0}>
              <Button onClick={() => run("graphics", "generate-graphics", "Graphics generated!", "graphics")} disabled={!a || busy.graphics}
                className="w-full mt-2 rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 font-medium disabled:opacity-40" data-testid="graphics-btn">
                {busy.graphics ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Painting…</> : "Generate art"}<CostHint endpoint="generate-graphics" />
              </Button>
            </PipelineStep>

            <PipelineStep n={4} icon={Mic} title="Voiceover script" done={!!project.script}>
              <Button onClick={() => run("script", "generate-script", "Script ready!", "voiceover")} disabled={!a || busy.script}
                className="w-full mt-2 rounded-full bg-white text-black hover:bg-white/90 font-medium disabled:opacity-40" data-testid="script-btn">
                {busy.script ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Writing…</> : "Write narration"}<CostHint endpoint="generate-script" />
              </Button>
            </PipelineStep>

            <PipelineStep n={5} icon={Share2} title="Viral captions" done={!!project.captions}>
              <Button onClick={() => run("captions", "generate-captions", "Captions ready!", "captions")} disabled={!a || busy.captions}
                className="w-full mt-2 rounded-full bg-[#39FF14] text-black hover:bg-[#39FF14]/90 font-medium disabled:opacity-40" data-testid="captions-btn">
                {busy.captions ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Writing…</> : "Generate captions"}
              </Button>
            </PipelineStep>
          </aside>

          {/* RIGHT */}
          <div className="space-y-6">
            {/* canvas */}
            <div className="bg-zinc-950 border border-white/10 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clapperboard className="w-4 h-4 text-[#00E5FF]" />
                  <span className="font-display text-sm tracking-tight">Preview canvas</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/50">3D / HD look</span>
                  <Switch checked={is3D} onCheckedChange={setIs3D} data-testid="toggle-3d" />
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video flex items-center justify-center">
                {hasVideo ? (
                  <video key={project.video.storage_path} controls ref={videoRef}
                    onLoadedMetadata={(e) => { const d = e.target.duration || 0; setDuration(d); setTrim((t) => (t[1] ? t : [0, d])); }}
                    className={`w-full h-full object-contain preview-3d ${is3D ? "active" : ""}`}
                    src={fileUrl(project.video.storage_path)} data-testid="video-preview" />
                ) : (
                  <div className="text-center text-white/40" data-testid="no-video-placeholder">
                    <Film className="w-10 h-10 mx-auto mb-3" />
                    <p className="font-light">Add a video (optional) — or just write your story and generate a cinematic film below.</p>
                  </div>
                )}
              </div>
              {t && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  <Stat label="Color grade" value={t.color_grade} />
                  <Stat label="3D style" value={t.depth_3d} />
                  <Stat label="Quality" value={t.hd_target} />
                  <div className="bg-black border border-[#39FF14]/30 rounded-lg p-3 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#39FF14] shrink-0" />
                    <span className="text-xs text-[#39FF14]">Copyright-safe</span>
                  </div>
                </div>
              )}
            </div>

            {/* TIMELINE & RENDER */}
            <div className="bg-zinc-950 border border-white/10 rounded-lg p-6" data-testid="timeline-panel">
              <div className="flex items-center gap-2 mb-4">
                <Scissors className="w-4 h-4 text-[#FF007F]" />
                <span className="font-display text-sm tracking-tight">Timeline & film — edit{hasVideo ? ", trim" : ""} & render</span>
              </div>
              {false ? (
                <p className="text-sm text-white/40 font-light">Upload a video to edit the timeline.</p>
              ) : (
                <div className="space-y-5">
                  {hasVideo && (<>
                  {/* trim */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-white/50">Trim</label>
                      <span className="text-xs text-[#00E5FF] font-display" data-testid="trim-readout">
                        {trim[0].toFixed(1)}s → {(trim[1] || duration).toFixed(1)}s
                      </span>
                    </div>
                    <div className="relative h-12 rounded-md bg-black border border-white/10 overflow-hidden flex items-center px-2">
                      <div className="flex-1">
                        <Slider min={0} max={duration || 100} step={0.1} value={[trim[0], trim[1] || duration]}
                          onValueChange={(v) => setTrim(v)} data-testid="trim-slider" />
                      </div>
                    </div>
                  </div>

                  {/* ordered steps */}
                  <div>
                    <label className="text-xs text-white/50">Layers (top applied first — reorder to change stacking)</label>
                    <div className="space-y-2 mt-2">
                      {steps.map((s, i) => (
                        <div key={s.type} className="bg-black border border-white/10 rounded-lg p-3" data-testid={`step-${s.type}`}>
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col">
                              <button onClick={() => moveStep(i, -1)} className="text-white/30 hover:text-white" data-testid={`step-up-${s.type}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                              <button onClick={() => moveStep(i, 1)} className="text-white/30 hover:text-white" data-testid={`step-down-${s.type}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                            </div>
                            <Switch checked={s.enabled} onCheckedChange={(v) => updateStep(i, { enabled: v })} data-testid={`step-toggle-${s.type}`} />
                            <span className={`text-sm flex-1 ${s.enabled ? "text-white" : "text-white/40"}`}>{s.label}</span>
                            {(s.type === "intro" || s.type === "overlay") && s.enabled && (
                              <Select value={s.asset_id || (assets[0]?.id || "")} onValueChange={(v) => updateStep(i, { asset_id: v })}>
                                <SelectTrigger className="bg-zinc-950 border-white/15 h-8 w-32 text-xs" data-testid={`step-asset-${s.type}`}><SelectValue placeholder="Graphic" /></SelectTrigger>
                                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                  {assets.map((as, idx) => <SelectItem key={as.id} value={as.id}>Graphic {idx + 1}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                            {s.type === "audio" && s.enabled && (
                              <Select value={renderVoice} onValueChange={setRenderVoice}>
                                <SelectTrigger className="bg-zinc-950 border-white/15 h-8 w-40 text-xs" data-testid="render-voice-select"><SelectValue placeholder="Voice" /></SelectTrigger>
                                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                  {buildVoiceItems(voiceCatalog, project).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                            {s.type === "subtitles" && s.enabled && (
                              <Select value={renderSubSize} onValueChange={setRenderSubSize}>
                                <SelectTrigger className="bg-zinc-950 border-white/15 h-8 w-32 text-xs" data-testid="render-caption-size"><SelectValue placeholder="Caption size" /></SelectTrigger>
                                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                  <SelectItem value="small">Small captions</SelectItem>
                                  <SelectItem value="medium">Medium captions</SelectItem>
                                  <SelectItem value="large">Large captions</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            {s.type === "overlay" && s.enabled && (
                              <Select value={s.position} onValueChange={(v) => updateStep(i, { position: v })}>
                                <SelectTrigger className="bg-zinc-950 border-white/15 h-8 w-28 text-xs" data-testid="step-pos-overlay"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                  <SelectItem value="top-left">Top left</SelectItem>
                                  <SelectItem value="top-right">Top right</SelectItem>
                                  <SelectItem value="bottom-left">Bottom left</SelectItem>
                                  <SelectItem value="bottom-right">Bottom right</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  </>)}

                  <div>
                    <label className="text-xs text-white/50">Cinematic motion — push-in &amp; parallax drift strength</label>
                    <div className="grid grid-cols-3 gap-2 mt-2" data-testid="motion-strength-group">
                      {[
                        { key: "subtle", label: "Subtle" },
                        { key: "standard", label: "Standard" },
                        { key: "dynamic", label: "Dynamic" },
                      ].map((m) => (
                        <button
                          key={m.key}
                          onClick={() => setRenderMotion(m.key)}
                          data-testid={`motion-${m.key}`}
                          className={`rounded-lg border px-3 py-2 text-sm transition-colors ${renderMotion === m.key ? "border-[#00E5FF] bg-[#00E5FF]/10 text-white" : "border-white/10 bg-black text-white/50 hover:text-white/80"}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {hasVideo && (
                  <p className="text-[11px] text-white/40 leading-snug" data-testid="render-note">
                    Your final video is always a brand-new cinematic 3D film generated from your story below — your uploaded clip is only used as inspiration, never exported as the result.
                  </p>
                  )}

                  <div className="rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-3.5 space-y-2" data-testid="film-panel">
                    <div className="flex items-center gap-2 text-sm text-white font-medium">
                      <Sparkles className="w-4 h-4 text-[#00E5FF]" /> Generate AI Cinematic Film
                    </div>
                    <p className="text-xs text-white/55 leading-relaxed">
                      {hasVideo
                        ? "Replaces your clip with a fully AI-generated cinematic story — character-consistent 3D scenes, narration, subtitles & motion, timed to the whole storyline. Uses the voice & motion settings above."
                        : "Generates a fully AI-made cinematic story — character-consistent 3D scenes, narration, subtitles & motion, timed to your whole storyline. Uses the voice & motion settings above."}
                    </p>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px] text-white/45">Scenes</label>
                        <Select value={sceneMode} onValueChange={setSceneMode}>
                          <SelectTrigger className="bg-zinc-950 border-white/15 h-8 text-xs mt-1" data-testid="film-scene-mode"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-white">
                            <SelectItem value="short">Short · fewer scenes</SelectItem>
                            <SelectItem value="balanced">Balanced</SelectItem>
                            <SelectItem value="detailed">Detailed · more scenes</SelectItem>
                            <SelectItem value="custom">Custom count</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[11px] text-white/45">Music bed</label>
                        <Select value={musicBed} onValueChange={setMusicBed}>
                          <SelectTrigger className="bg-zinc-950 border-white/15 h-8 text-xs mt-1" data-testid="film-music-bed"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-white">
                            <SelectItem value="none">No music</SelectItem>
                            <SelectItem value="calm">Calm</SelectItem>
                            <SelectItem value="uplifting">Uplifting</SelectItem>
                            <SelectItem value="dramatic">Dramatic</SelectItem>
                            <SelectItem value="dreamy">Dreamy</SelectItem>
                            <SelectItem value="moody">Moody</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {sceneMode === "custom" && (
                      <div>
                        <label className="text-[11px] text-white/45">Number of scenes (2–10)</label>
                        <Input type="number" min={2} max={10} value={sceneCount}
                          onChange={(e) => setSceneCount(Math.max(2, Math.min(10, parseInt(e.target.value || "5", 10))))}
                          className="bg-zinc-950 border-white/15 h-8 text-xs mt-1" data-testid="film-scene-count" />
                      </div>
                    )}

                    {musicBed !== "none" && (
                      <div data-testid="film-music-volume">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] text-white/45 flex items-center gap-1"><Volume2 className="w-3 h-3" />Music volume (under voice)</label>
                          <span className="text-[11px] text-white/60">{Math.round(filmMusicVol * 100)}%</span>
                        </div>
                        <Slider value={[filmMusicVol]} min={0} max={0.6} step={0.02}
                          onValueChange={(v) => setFilmMusicVol(v[0])} className="mt-2" data-testid="film-music-volume-slider" />
                      </div>
                    )}

                    <div>
                      <label className="text-[11px] text-white/45 flex items-center gap-1"><Film className="w-3 h-3" />Scene transition</label>
                      <Select value={filmTransition} onValueChange={setFilmTransition}>
                        <SelectTrigger className="bg-zinc-950 border-white/15 h-8 text-xs mt-1" data-testid="film-transition"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-white/10 text-white">
                          <SelectItem value="none">Hard cut · no blend</SelectItem>
                          <SelectItem value="crossfade">Crossfade · gentle dissolve</SelectItem>
                          <SelectItem value="dissolve">Dissolve · grainy blend</SelectItem>
                          <SelectItem value="slide">Slide · push across</SelectItem>
                          <SelectItem value="wipe">Wipe · reveal</SelectItem>
                        </SelectContent>
                      </Select>
                      <div key={filmTransition} className={`tdemo tdemo-${filmTransition} mt-2`} data-testid="transition-preview" aria-label={`${filmTransition} preview`}>
                        <div className="tile a">Scene A</div>
                        <div className="tile b">Scene B</div>
                      </div>
                    </div>

                    <div data-testid="visual-style-wrap">
                      <label className="text-[11px] text-white/45 flex items-center gap-1"><Sparkles className="w-3 h-3" />Visual style</label>
                      <Select value={visualStyle} onValueChange={setVisualStyle}>
                        <SelectTrigger className="bg-zinc-950 border-white/15 h-8 text-xs mt-1" data-testid="visual-style-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-white/10 text-white">
                          <SelectItem value="animated">Cinematic 3D animation (default)</SelectItem>
                          <SelectItem value="photoreal">Photoreal presenter · best for lip-sync</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-white/45 leading-snug mt-1">Photoreal renders speakers as real people — talking-head scenes lip-sync far more reliably than stylized 3D faces.</p>
                    </div>

                    <div data-testid="story-style-wrap">
                      <label className="text-[11px] text-white/45 flex items-center gap-1"><Users className="w-3 h-3" />Story style</label>
                      <Select value={aiMotion ? "conversation" : storyStyle} onValueChange={setStoryStyle} disabled={aiMotion}>
                        <SelectTrigger className="bg-zinc-950 border-white/15 h-8 text-xs mt-1 disabled:opacity-60" data-testid="story-style-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-white/10 text-white">
                          <SelectItem value="cinematic">Cinematic narration (default)</SelectItem>
                          <SelectItem value="conversation">Conversation · back-and-forth dialogue</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-white/45 leading-snug mt-1" data-testid="story-style-hint">
                        {aiMotion
                          ? "AI motion films are dialogue-driven — every character speaks their own lines on screen (lip-synced), with no separate narrator voice-over."
                          : "Conversation writes the film as a back-and-forth exchange between your characters — each speaks in their own voice."}
                      </p>
                      <div className="mt-2" data-testid="dialogue-length-wrap">
                        <label className="text-[11px] text-white/45">{storyStyle === "conversation" ? "Dialogue length" : "Narration line length"}</label>
                        <Select value={dialogueLength} onValueChange={setDialogueLength}>
                          <SelectTrigger className="bg-zinc-950 border-white/15 h-8 text-xs mt-1" data-testid="dialogue-length-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-white">
                            <SelectItem value="punchy">Punchy · short, snappy lines</SelectItem>
                            <SelectItem value="balanced">Balanced (default)</SelectItem>
                            <SelectItem value="detailed">Detailed · fuller lines</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="rounded-lg border border-[#00E5FF]/25 bg-[#00E5FF]/5 p-3" data-testid="ai-motion-card">
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-[12px] text-white font-medium flex items-center gap-1.5" htmlFor="ai-motion-switch">
                          <Sparkles className="w-3.5 h-3.5 text-[#00E5FF]" /> Animate scenes — real AI motion
                        </label>
                        <Switch id="ai-motion-switch" checked={aiMotion} onCheckedChange={setAiMotion} data-testid="ai-motion-toggle" />
                      </div>
                      <p className="text-[11px] text-white/50 leading-relaxed mt-1.5">
                        {aiMotion
                          ? "Each scene becomes a real motion video (Kling) and every character speaks their own lines on screen — lip-synced in both cinematic 3D and photoreal, with no narrator voice-over. Adds a few minutes & more credits; scenes that can't animate in time fall back to smooth Ken Burns motion."
                          : "Off = fast cinematic Ken Burns motion with narrator voice-over (recommended for quick drafts). Turn on for true AI-generated motion where characters speak on screen."}
                      </p>
                      {aiMotion && (
                        <div className="mt-2" data-testid="motion-tier-wrap">
                          <label className="text-[11px] text-white/45">Motion quality</label>
                          <Select value={motionTier} onValueChange={setMotionTier}>
                            <SelectTrigger className="bg-zinc-950 border-white/15 h-8 text-xs mt-1" data-testid="motion-tier-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              <SelectItem value="kling_v2_1_standard">Kling v2.1 Standard · balanced (recommended)</SelectItem>
                              <SelectItem value="kling_v1_6_standard">Kling v1.6 Standard · faster & cheaper</SelectItem>
                              <SelectItem value="kling_v2_master">Kling v2 Master · highest quality (slowest)</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-white/10">
                            <label className="text-[11px] text-white font-medium flex items-center gap-1.5" htmlFor="lip-sync-switch">
                              <Mic className="w-3 h-3 text-[#39FF14]" /> Lip-sync character dialogue
                            </label>
                            <Switch id="lip-sync-switch" checked={lipSync} onCheckedChange={setLipSync} data-testid="lip-sync-toggle" />
                          </div>
                          <p className="text-[10px] text-white/45 leading-snug mt-1">Match character voices to mouth movements when a character is visibly speaking. Narrator-only scenes stay as voice-over. Falls back to motion + voice-over if unavailable.</p>
                        </div>
                      )}
                    </div>


                    <div data-testid="film-narrator-voice">
                      <label className="text-[11px] text-white/45 flex items-center gap-1"><Mic className="w-3 h-3" />Narrator voice</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Select value={renderVoice} onValueChange={setRenderVoice}>
                          <SelectTrigger className="bg-zinc-950 border-white/15 h-8 text-xs flex-1" data-testid="film-voice-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-white">
                            {buildVoiceItems(voiceCatalog, project).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="icon" onClick={() => previewVoiceToken(renderVoice)} disabled={previewing}
                          className="h-8 w-8 rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white shrink-0" data-testid="film-voice-preview" title="Preview voice">
                          {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="rounded-lg border border-white/10 bg-black/40 p-3" data-testid="personal-voice-card">
                      <div className="flex items-center gap-1.5 text-[12px] text-white font-medium mb-1">
                        <Mic className="w-3.5 h-3.5 text-[#FF007F]" /> Personal Voice Over
                      </div>
                      <input ref={voiceFileRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm" className="hidden" onChange={onCloneVoiceFile} data-testid="voice-clone-input" />
                      {voiceCatalog?.personal?.available ? (
                        <div className="flex items-center justify-between gap-2" data-testid="personal-voice-ready">
                          <span className="text-[11px] text-[#39FF14] flex items-center gap-1"><Check className="w-3 h-3" />Account voice cloned · pick “My Voice · cloned” above</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <button onClick={() => previewVoiceToken("personal:account")} disabled={previewing}
                              className="text-[11px] text-white/70 hover:text-[#00E5FF] flex items-center gap-1 disabled:opacity-40" data-testid="personal-voice-hear-btn">
                              <Play className="w-3 h-3" />Hear
                            </button>
                            <button onClick={() => removePersonalVoice("account")} disabled={busy.clonevoice}
                              className="text-[11px] text-white/40 hover:text-[#FF007F]" data-testid="personal-voice-remove">Remove</button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-[11px] text-white/50 leading-relaxed mb-2">Record or upload a clear 10–30s sample of your voice and narrate any film in your own voice.</p>
                      )}
                      <label className="flex items-start gap-2 mt-2 cursor-pointer" data-testid="voice-consent-label">
                        <input type="checkbox" checked={voiceConsent} onChange={(e) => setVoiceConsent(e.target.checked)}
                          className="mt-0.5 accent-[#FF007F]" data-testid="voice-consent-checkbox" />
                        <span className="text-[10px] text-white/45 leading-snug">This is my own voice, or I have the speaker’s consent to clone it. My sample stays private to my account.</span>
                      </label>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {recording ? (
                          <Button onClick={stopRecording} size="sm"
                            className="h-7 rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 text-[11px] px-3 animate-pulse" data-testid="voice-record-stop-btn">
                            <span className="w-2 h-2 rounded-full bg-white mr-1.5 inline-block" />Stop · {recSeconds}s
                          </Button>
                        ) : (
                          <Button onClick={() => startRecording(cloneScopeRef.current || "account")} disabled={busy.clonevoice || !voiceConsent} size="sm"
                            className="h-7 rounded-full bg-white/10 text-white hover:bg-white/20 text-[11px] px-3 disabled:opacity-40" data-testid="voice-record-btn">
                            <Mic className="w-3 h-3 mr-1" />Record voice
                          </Button>
                        )}
                        <Button onClick={() => startCloneVoice("account")} disabled={busy.clonevoice || !voiceConsent || recording} size="sm"
                          className="h-7 rounded-full bg-white/10 text-white hover:bg-white/20 text-[11px] px-3 disabled:opacity-40" data-testid="clone-voice-account-btn">
                          {busy.clonevoice ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Cloning…</> : <><Upload className="w-3 h-3 mr-1" />{voiceCatalog?.personal?.available ? "Re-clone (account)" : "Upload sample"}</>}
                        </Button>
                        <Button onClick={() => startCloneVoice("project")} disabled={busy.clonevoice || !voiceConsent || recording} variant="outline" size="sm"
                          className="h-7 rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white text-[11px] px-3 disabled:opacity-40" data-testid="clone-voice-project-btn">
                          This project only
                        </Button>
                      </div>
                      {project.personal_voice?.voice_id && project.personal_voice.voice_id !== voiceCatalog?.personal?.voice_id && (
                        <p className="text-[11px] text-[#00E5FF] mt-2" data-testid="project-voice-ready">Project voice cloned · pick “My Voice · this project” above</p>
                      )}
                    </div>


                    <div className="flex items-center gap-2 pt-1">
                      {project.character_ref_image ? (
                        <div className="flex items-center gap-2" data-testid="charref-preview">
                          <img src={fileUrl(project.character_ref_image.storage_path)} alt="Character reference"
                            className="w-9 h-9 rounded object-cover border border-[#00E5FF]/40" />
                          <span className="text-[11px] text-white/60">{visualStyle === "photoreal" ? "Presenter face locked" : "Character locked"}</span>
                          <button onClick={removeCharRef} disabled={busy.charref} className="text-[11px] text-white/40 hover:text-[#FF007F]" data-testid="charref-remove">Remove</button>
                        </div>
                      ) : (
                        <label className="text-[11px] text-white/55 hover:text-white cursor-pointer flex items-center gap-1.5" data-testid="charref-upload-label">
                          <Upload className="w-3.5 h-3.5" /> {busy.charref ? "Uploading…" : (visualStyle === "photoreal" ? "Upload a presenter photo to lock this face" : "Upload character reference (optional)")}
                          <input type="file" accept="image/*" className="hidden" onChange={uploadCharRef} disabled={busy.charref} data-testid="charref-upload-input" />
                        </label>
                      )}
                    </div>

                    <Button onClick={generateFilm} disabled={busy.film || busy.render || project.render_status?.running}
                      className="w-full rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-semibold disabled:opacity-40" data-testid="generate-film-btn">
                      {(busy.film || project.render_status?.running) ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{project.render_status?.step || "Generating film"}…</> : <><Sparkles className="w-4 h-4 mr-1.5" />Generate cinematic film</>}<CostHint endpoint="film" />
                    </Button>

                    {(project.film_scenes?.length > 0) && !project.render_status?.running && (
                      <div className="pt-1 space-y-2" data-testid="film-scenes">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-white/45">Scene editor — drag to reorder, lock, edit or reshoot before rendering</span>
                          <div className="flex items-center gap-1.5">
                            <div className="flex items-center gap-1" data-testid="revoice-all-row">
                              <Mic className="w-3 h-3 text-white/40" />
                              <Select value="" onValueChange={(v) => revoiceFilm(v)} disabled={busy.revoice}>
                                <SelectTrigger className="bg-zinc-950 border-white/15 h-7 w-[132px] text-[11px]" data-testid="revoice-all-select">
                                  <SelectValue placeholder={busy.revoice ? "Re-voicing…" : "Voice all scenes"} />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                  {buildVoiceItems(voiceCatalog, project).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <Button onClick={startPreview} disabled={previewIdx !== null} size="sm"
                              className="h-7 rounded-full bg-white/10 text-white hover:bg-white/20 text-[11px] px-3" data-testid="preview-film-btn">
                              <Play className="w-3 h-3 mr-1" />Preview
                            </Button>
                            <Button onClick={reassembleFilm} disabled={busy.film} size="sm"
                              className="h-7 rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 text-[11px] px-3" data-testid="reassemble-film-btn">
                              Re-render film<CostHint endpoint="render" />
                            </Button>
                          </div>
                        </div>

                        <div className="rounded-lg border border-white/10 bg-black/30" data-testid="character-voices-section">
                          <button onClick={() => setShowCharVoices((v) => !v)}
                            className="w-full flex items-center justify-between px-3 py-2 text-[11px] text-white/70 hover:text-white" data-testid="character-voices-toggle">
                            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#00E5FF]" />Character voices — assign a narrator per character (optional)</span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${showCharVoices ? "rotate-180" : ""}`} />
                          </button>
                          {showCharVoices && (
                            <div className="px-3 pb-3 space-y-2" data-testid="character-voices-body">
                              {distinctCharacters().map((name) => {
                                const face = project.film?.character_refs?.[name];
                                return (
                                <div key={name} className="flex items-center gap-2" data-testid={`character-row-${name}`}>
                                  <span className="text-[11px] text-white/70 w-20 truncate" title={name}>{name}</span>
                                  <Select value={charVoices[name] || ""} onValueChange={(v) => setCharVoices((m) => ({ ...m, [name]: v }))}>
                                    <SelectTrigger className="bg-zinc-950 border-white/15 h-7 text-[11px] flex-1" data-testid={`character-voice-${name}`}>
                                      <SelectValue placeholder="Use film default voice" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                      {buildVoiceItems(voiceCatalog, project).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  <button onClick={() => previewVoiceToken(charVoices[name] || project.film?.characters?.[name] || renderVoice)} disabled={previewing}
                                    className="shrink-0 w-7 h-7 rounded-full bg-white/5 text-white/60 hover:text-[#39FF14] flex items-center justify-center disabled:opacity-40"
                                    data-testid={`character-voice-preview-${name}`} title="Hear this voice">
                                    {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                                  </button>
                                  {name !== "Narrator" && (face ? (
                                    <div className="flex items-center gap-1 shrink-0" data-testid={`character-face-preview-${name}`}>
                                      <img src={fileUrl(face.storage_path)} alt={`${name} face`} className="w-7 h-7 rounded object-cover border border-[#FF007F]/50" title={`${name}'s locked face`} />
                                      <button onClick={() => removeCharacterFace(name)} disabled={busy[`face-${name}`]}
                                        className="text-[10px] text-white/40 hover:text-[#FF007F] disabled:opacity-30" data-testid={`character-face-remove-${name}`}>
                                        {busy[`face-${name}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : "×"}
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="shrink-0 text-[10px] text-white/45 hover:text-white cursor-pointer flex items-center gap-1 px-2 py-1 rounded-full bg-white/5" data-testid={`character-face-upload-${name}`} title="Lock this character's face with a photo">
                                      {busy[`face-${name}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3" />Face</>}
                                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadCharacterFace(name, e.target.files?.[0]); e.target.value = ""; }} disabled={busy[`face-${name}`]} />
                                    </label>
                                  ))}
                                </div>
                                );
                              })}
                              <p className="text-[10px] text-white/40 leading-snug">Assign each character a voice and lock their face with a photo — every scene (and split) keeps that speaker’s voice and look. Reshoot or re-render to apply a new face.</p>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button onClick={saveCharacterVoices} disabled={busy.charvoices} size="sm"
                                  className="h-7 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 text-[11px] px-3" data-testid="save-character-voices-btn">
                                  {busy.charvoices ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Applying…</> : "Apply character voices"}<CostHint endpoint="voiceover" />
                                </Button>
                                <Button onClick={recastVoices} disabled={busy.recast || project.render_status?.running} size="sm" variant="outline"
                                  className="h-7 rounded-full border-[#39FF14]/40 bg-transparent text-[#39FF14] hover:bg-[#39FF14]/10 text-[11px] px-3" data-testid="recast-voices-btn" title="Smart-recast every character voice by gender, age & accent">
                                  {busy.recast ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Recasting…</> : <><Wand2 className="w-3 h-3 mr-1" />Smart recast</>}
                                </Button>
                                {project.film?.voice_undo && (
                                  <Button onClick={restoreVoices} disabled={busy.restore || project.render_status?.running} size="sm" variant="ghost"
                                    className="h-7 rounded-full text-white/50 hover:text-white text-[11px] px-3" data-testid="restore-voices-btn" title="Undo the last smart recast">
                                    {busy.restore ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Undo2 className="w-3 h-3 mr-1" />Undo recast</>}
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {previewIdx !== null && orderedScenes()[previewIdx] && (
                          <div className="rounded-xl border border-[#00E5FF]/40 bg-black p-3 space-y-2" data-testid="timeline-preview">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-white/60">Preview · Scene {previewIdx + 1} of {orderedScenes().length}</span>
                              <button onClick={() => setPreviewIdx(null)} className="text-white/40 hover:text-white" data-testid="preview-close"><X className="w-4 h-4" /></button>
                            </div>
                            <div className="relative overflow-hidden rounded-lg">
                              <img src={fileUrl(orderedScenes()[previewIdx].storage_path)} alt="preview"
                                className="w-full max-h-72 object-contain bg-black animate-[kenburns_6s_ease-out]" key={previewIdx} />
                            </div>
                            <p className="text-[11px] text-white/70 leading-snug min-h-[2rem]">{orderedScenes()[previewIdx].narration}</p>
                            <div className="flex items-center gap-1.5 text-[10px] text-white/50" data-testid="preview-speaker">
                              <Users className="w-3 h-3 text-[#00E5FF]" />
                              <span className="text-white/70">{orderedScenes()[previewIdx].character || "Narrator"}</span>
                              <span>·</span>
                              <Volume2 className="w-3 h-3" />
                              <span>{voiceLabel(orderedScenes()[previewIdx].voice)}</span>
                            </div>
                            {orderedScenes()[previewIdx].audio_url && (
                              <audio key={previewIdx} ref={previewAudioRef} src={fileUrl(orderedScenes()[previewIdx].audio_url)} autoPlay controls
                                onEnded={advancePreview} className="w-full h-8" data-testid="preview-audio" />
                            )}
                            <div className="flex items-center justify-center gap-1.5">
                              {orderedScenes().map((s, i) => (
                                <button key={s.id} onClick={() => setPreviewIdx(i)}
                                  className={`h-1.5 rounded-full transition-all ${i === previewIdx ? "w-6 bg-[#00E5FF]" : "w-1.5 bg-white/25"}`}
                                  data-testid={`preview-dot-${i}`} />
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="space-y-2">
                          {dialogueSceneCount() >= 1 && (
                            <div className="flex items-center justify-between gap-2 rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/5 p-2.5" data-testid="auto-split-all-banner">
                              <div className="flex items-center gap-2 min-w-0">
                                <Wand2 className="w-4 h-4 text-[#39FF14] shrink-0" />
                                <span className="text-[11px] text-white/75 leading-snug truncate">
                                  {dialogueSceneCount()} scene{dialogueSceneCount() > 1 ? "s" : ""} contain{dialogueSceneCount() > 1 ? "" : "s"} multi-speaker dialogue. Split them all into per-speaker shots at once.
                                </span>
                              </div>
                              <Button size="sm" onClick={autoSplitAll} disabled={busy.splitall || project.render_status?.running}
                                className="h-7 shrink-0 text-[11px] rounded-full bg-[#39FF14] text-black hover:bg-[#39FF14]/85 px-3" data-testid="auto-split-all-btn">
                                {busy.splitall ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Auto-split all</>}
                              </Button>
                            </div>
                          )}
                          {[...project.film_scenes].sort((a, b) => a.index - b.index).map((sc, pos) => (
                            <div key={sc.id}
                              draggable
                              onDragStart={() => setDragIndex(pos)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => onSceneDrop(dragIndex, pos)}
                              className={`flex gap-2 rounded-lg border p-2 bg-black/40 ${dragIndex === pos ? "border-[#00E5FF]" : "border-white/10"} ${sc.locked ? "ring-1 ring-[#00E5FF]/40" : ""}`}
                              data-testid={`film-scene-${sc.index}`}>
                              <div className="flex flex-col items-center justify-center text-white/30 cursor-grab active:cursor-grabbing" data-testid={`scene-drag-${sc.index}`}>
                                <GripVertical className="w-4 h-4" />
                                <span className="text-[10px] text-white/40">#{sc.index + 1}</span>
                              </div>
                              <img src={fileUrl(sc.storage_path)} alt={`Scene ${sc.index + 1}`} className="w-14 h-24 rounded object-cover flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                {editingScene === sc.index ? (
                                  <div className="space-y-1.5">
                                    <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3}
                                      className="bg-zinc-950 border-white/15 text-xs" data-testid={`scene-narration-input-${sc.index}`} />
                                    <div className="flex flex-wrap gap-1.5">
                                      <Button size="sm" onClick={() => saveSceneNarration(sc.index, false)} disabled={busy[`narr-${sc.index}`]}
                                        className="h-6 text-[11px] rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 px-2.5" data-testid={`scene-save-voice-${sc.index}`}>
                                        {busy[`narr-${sc.index}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save & re-voice"}
                                      </Button>
                                      <Button size="sm" onClick={() => saveSceneNarration(sc.index, true)} disabled={busy[`narr-${sc.index}`]}
                                        className="h-6 text-[11px] rounded-full bg-white/10 text-white hover:bg-white/20 px-2.5" data-testid={`scene-save-reshoot-${sc.index}`}>
                                        Save, re-voice &amp; reshoot
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingScene(null)} className="h-6 text-[11px] text-white/40 px-2">Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-[11px] text-white/70 line-clamp-3 leading-snug">{sc.narration || sc.prompt || `Scene ${sc.index + 1}`}</p>
                                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                                      {sc.character && sc.character !== "Narrator" && (
                                        <span className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/60" data-testid={`scene-character-${sc.index}`}>
                                          <Users className="w-3 h-3" />{sc.character}
                                        </span>
                                      )}
                                      {sc.lip_sync_status && sc.lip_sync_status !== "not_required" && (
                                        <span className={`text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full ${sc.lip_sync_status === "completed" ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-amber-500/15 text-amber-400"}`} data-testid={`scene-lipsync-${sc.index}`}>
                                          <Mic className="w-3 h-3" />{sc.lip_sync_status === "completed" ? "Lip-synced" : "Voice-over"}
                                        </span>
                                      )}
                                      {aiMotion && lipSync && sc.character && sc.character !== "Narrator" && (
                                        <button onClick={() => toggleSceneLipSync(sc.index, !sc.lip_sync_off)} disabled={busy[`ls-${sc.index}`] || sc.locked}
                                          className={`text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full disabled:opacity-30 ${sc.lip_sync_off ? "bg-white/5 text-white/50 hover:text-white" : "bg-[#00E5FF]/15 text-[#00E5FF]"}`}
                                          data-testid={`scene-lipsync-toggle-${sc.index}`}>
                                          {busy[`ls-${sc.index}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />}
                                          {sc.lip_sync_off ? "Lip sync off" : "Lip sync on"}
                                        </button>
                                      )}
                                      <button onClick={() => toggleSceneLock(sc.index, !sc.locked)} disabled={busy[`lock-${sc.index}`]}
                                        className={`text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full ${sc.locked ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "bg-white/5 text-white/50 hover:text-white"}`}
                                        data-testid={`scene-lock-${sc.index}`}>
                                        {sc.locked ? <><Lock className="w-3 h-3" />Locked</> : <><Unlock className="w-3 h-3" />Lock</>}
                                      </button>
                                      <button onClick={() => { setEditingScene(sc.index); setEditText(sc.narration || ""); }} disabled={sc.locked}
                                        className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/50 hover:text-white disabled:opacity-30"
                                        data-testid={`scene-edit-${sc.index}`}>
                                        <Pencil className="w-3 h-3" />Edit narration
                                      </button>
                                      <button onClick={() => regenerateScene(sc.index)} disabled={busy[`scene-${sc.index}`] || sc.locked}
                                        className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/50 hover:text-white disabled:opacity-30"
                                        data-testid={`regenerate-scene-${sc.index}`}>
                                        {busy[`scene-${sc.index}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Wand2 className="w-3 h-3" />Reshoot</>}
                                      </button>
                                      {project.film?.ai_motion && sc.final_motion_type === "ken_burns" && !project.render_status?.running && (
                                        <button onClick={() => reanimateScene(sc.index)} disabled={busy[`reanim-${sc.index}`] || sc.locked}
                                          className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] hover:bg-[#00E5FF]/25 disabled:opacity-30"
                                          data-testid={`reanimate-scene-${sc.index}`}
                                          title="This scene fell back to Ken Burns — retry real AI motion for just this shot">
                                          {busy[`reanim-${sc.index}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3" />Reanimate</>}
                                        </button>
                                      )}
                                      <button onClick={() => deleteScene(sc.index)} disabled={busy[`del-${sc.index}`] || project.film_scenes.length <= 1}
                                        className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/50 hover:text-[#FF007F] disabled:opacity-30"
                                        data-testid={`delete-scene-${sc.index}`}>
                                        {busy[`del-${sc.index}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Trash2 className="w-3 h-3" />Delete</>}
                                      </button>
                                      <button onClick={() => playSceneVoice(sc.audio_url)}
                                        className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/50 hover:text-[#39FF14]"
                                        data-testid={`scene-voice-preview-${sc.index}`} title="Hear this scene's voice">
                                        <Volume2 className="w-3 h-3" />Preview voice
                                      </button>
                                      <button onClick={() => setSceneStyle(sc.index, (sc.visual_style || visualStyle) === "photoreal" ? "animated" : "photoreal")}
                                        disabled={busy[`style-${sc.index}`] || sc.locked}
                                        className={`text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full disabled:opacity-30 ${(sc.visual_style || visualStyle) === "photoreal" ? "bg-[#FF007F]/15 text-[#FF007F]" : "bg-white/5 text-white/50 hover:text-white"}`}
                                        data-testid={`scene-style-toggle-${sc.index}`} title="Switch this scene's look">
                                        {busy[`style-${sc.index}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                        {(sc.visual_style || visualStyle) === "photoreal" ? "Photoreal" : "Animated"}
                                      </button>
                                      {detectDialogue(sc.narration).length >= 2 && (
                                        <button onClick={() => autoSplitScene(sc.index, detectDialogue(sc.narration))}
                                          disabled={busy.split || sc.locked || (project.film_scenes.length + detectDialogue(sc.narration).length - 1) > 10}
                                          className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#39FF14]/15 text-[#39FF14] disabled:opacity-30"
                                          data-testid={`scene-autosplit-${sc.index}`} title="Split the detected dialogue into per-speaker shots">
                                          {busy.split ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                                          Auto-split ({detectDialogue(sc.narration).length})
                                        </button>
                                      )}
                                      <button onClick={() => { setSplitScene(sc.index); setSplitText(`${sc.character || "Amara"}: ${sc.narration || ""}\nKwame: `); }}
                                        disabled={sc.locked || project.film_scenes.length >= 10}
                                        className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/50 hover:text-white disabled:opacity-30"
                                        data-testid={`scene-split-${sc.index}`} title="Split into a multi-speaker exchange">
                                        <Users className="w-3 h-3" />Split speakers
                                      </button>
                                    </div>
                                    {splitScene === sc.index && (
                                      <div className="mt-2 rounded-lg border border-[#00E5FF]/30 bg-black/50 p-2" data-testid={`scene-split-panel-${sc.index}`}>
                                        <label className="text-[10px] text-white/60">One line per speaker — e.g. <span className="text-white/80">Amara: Hello there</span></label>
                                        <textarea value={splitText} onChange={(e) => setSplitText(e.target.value)} rows={3}
                                          className="w-full mt-1 rounded bg-zinc-950 border border-white/15 text-[11px] text-white p-2 leading-snug"
                                          placeholder={"Amara: Believe in your light.\nKwame: And share it."} data-testid={`scene-split-input-${sc.index}`} />
                                        <div className="flex items-center gap-2 mt-1.5">
                                          <Button size="sm" onClick={submitSplitScene} disabled={busy.split}
                                            className="h-6 text-[11px] bg-[#00E5FF] text-black hover:bg-[#00E5FF]/80 px-2" data-testid={`scene-split-submit-${sc.index}`}>
                                            {busy.split ? <Loader2 className="w-3 h-3 animate-spin" /> : "Split into shots"}
                                          </Button>
                                          <Button size="sm" variant="ghost" onClick={() => { setSplitScene(null); setSplitText(""); }} className="h-6 text-[11px] text-white/40 px-2">Cancel</Button>
                                        </div>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1.5 mt-1.5" data-testid={`scene-duration-${sc.index}`}>
                                      <Clock className="w-3 h-3 text-white/40" />
                                      <button onClick={() => nudgeSceneDuration(sc.index, sc.duration, -0.5)} disabled={busy[`dur-${sc.index}`]}
                                        className="w-5 h-5 rounded bg-white/5 text-white/60 hover:text-white flex items-center justify-center text-xs" data-testid={`scene-duration-minus-${sc.index}`}>−</button>
                                      <span className="text-[11px] text-white/60 w-10 text-center">{(sc.duration || 0).toFixed(1)}s</span>
                                      <button onClick={() => nudgeSceneDuration(sc.index, sc.duration, 0.5)} disabled={busy[`dur-${sc.index}`]}
                                        className="w-5 h-5 rounded bg-white/5 text-white/60 hover:text-white flex items-center justify-center text-xs" data-testid={`scene-duration-plus-${sc.index}`}>+</button>
                                      <Select value={sc.voice || renderVoice} onValueChange={(v) => setSceneVoice(sc.index, v)} disabled={sc.locked || busy[`voice-${sc.index}`]}>
                                        <SelectTrigger className="bg-zinc-950 border-white/15 h-6 w-36 text-[10px] ml-auto" data-testid={`scene-voice-${sc.index}`}><SelectValue placeholder="Voice" /></SelectTrigger>
                                        <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                          {buildVoiceItems(voiceCatalog, project).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                          <Button onClick={() => addScene(null)} disabled={busy.addscene || project.film_scenes.length >= 10} variant="outline"
                            className="w-full h-8 rounded-lg border-dashed border-white/20 bg-transparent text-white/60 hover:text-white hover:border-[#00E5FF]/50 text-xs" data-testid="add-scene-btn">
                            {busy.addscene ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Adding…</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />Add a scene</>}<CostHint endpoint="graphics" />
                          </Button>
                          {project.final_render && (project.film?.ai_motion || project.film_scenes?.some((s) => s.lip_sync_note && s.lip_sync_note !== "Voice-over")) && (
                            <div className="mt-3 rounded-lg border border-white/10 bg-black/40 p-3" data-testid="lip-sync-report">
                              <div className="flex items-center gap-1.5 text-[12px] text-white font-medium mb-2">
                                <Mic className="w-3.5 h-3.5 text-[#39FF14]" /> Lip-sync report
                                <span className="text-[10px] text-white/40 font-normal">
                                  {project.film?.lip_sync === false
                                    ? "Lip sync was off for this film"
                                    : `${project.film_scenes.filter((s) => s.lip_sync_status === "completed").length} of ${project.film_scenes.filter((s) => s.lip_sync_required).length} speaking scenes lip-synced`}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {[...project.film_scenes].sort((a, b) => a.index - b.index).map((s) => (
                                  <div key={s.id} className="flex items-center gap-2 text-[11px]" data-testid={`lip-sync-report-row-${s.index}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.lip_sync_status === "completed" ? "bg-[#39FF14]" : s.lip_sync_required ? "bg-amber-400" : "bg-white/25"}`} />
                                    <span className="text-white/50 shrink-0">Scene {s.index + 1}</span>
                                    <span className="text-white/70 truncate">{s.lip_sync_note}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {project.render_status?.running && (
                    <div className="pt-3" data-testid="render-progress">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-white/70 flex items-center gap-1.5" data-testid="render-progress-step">
                          <Loader2 className="w-3 h-3 animate-spin text-[#FF007F]" />{project.render_status.step || "Rendering"}…
                        </span>
                        <span className="text-white/50 tabular-nums" data-testid="render-progress-percent">{project.render_status.percent ?? 0}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#FF007F] to-[#00E5FF] transition-[width] duration-500 ease-out"
                          style={{ width: `${project.render_status.percent ?? 0}%` }} data-testid="render-progress-bar" />
                      </div>
                      <p className="text-[11px] text-white/40 mt-1.5">Rendering runs in the background — feel free to keep editing other tabs.</p>
                      {project.film?.ai_motion && (project.film?.scenes?.length > 0) && (
                        <div className="mt-3" data-testid="live-scene-grid">
                          <p className="text-[11px] text-white/45 mb-2">Scenes animating — each lights up as its motion is ready</p>
                          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                            {[...project.film.scenes].sort((a, b) => a.index - b.index).map((sc) => {
                              const done = !!sc.final_motion_type;
                              return (
                                <div key={sc.index}
                                  className={`relative aspect-[9/16] rounded-md overflow-hidden border transition-all duration-500 ${done ? "border-[#39FF14]/60 shadow-[0_0_12px_rgba(57,255,20,0.35)]" : "border-white/10"}`}
                                  data-testid={`live-scene-${sc.index}`} data-scene-ready={done ? "true" : "false"}>
                                  {sc.storage_path
                                    ? <img src={fileUrl(sc.storage_path)} alt={`Scene ${sc.index + 1}`}
                                        className={`w-full h-full object-cover transition-opacity duration-500 ${done ? "opacity-100" : "opacity-30"}`} />
                                    : <div className="w-full h-full bg-white/5" />}
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    {done
                                      ? <CheckCircle2 className="w-5 h-5 text-[#39FF14] drop-shadow" />
                                      : <Loader2 className="w-5 h-5 text-[#00E5FF] animate-spin" />}
                                  </div>
                                  <span className="absolute bottom-0.5 left-1 text-[9px] text-white/70 tabular-nums">{sc.index + 1}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {project.final_render && (
                    <div className="pt-2 float-up" data-testid="final-result">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-[#39FF14]">Final render</span>
                        <a href={fileUrl(project.final_render.storage_path)} download className="text-white/50 hover:text-[#00E5FF] flex items-center gap-1 text-xs" data-testid="download-final">
                          <Download className="w-3.5 h-3.5" /> Download
                        </a>
                      </div>

                      {/* Before / After comparison */}
                      {project.video && (
                        <div className="mb-2 flex items-center gap-2" data-testid="compare-toggle">
                          <div className="inline-flex rounded-full border border-white/10 bg-black p-0.5 text-xs">
                            <button onClick={() => setCompareMode("before")}
                              className={`px-3 py-1 rounded-full transition-colors ${compareMode === "before" ? "bg-white/10 text-white" : "text-white/45 hover:text-white"}`}
                              data-testid="compare-before-btn">Before</button>
                            <button onClick={() => setCompareMode("after")}
                              className={`px-3 py-1 rounded-full transition-colors ${compareMode === "after" ? "bg-[#39FF14] text-black font-medium" : "text-white/45 hover:text-white"}`}
                              data-testid="compare-after-btn">After · Viral3D</button>
                          </div>
                          {compareMode === "after" && (project.transform?.color_grade) && (
                            <span className="text-[10px] text-white/40 truncate max-w-[220px]" title={project.transform.color_grade} data-testid="compare-grade-label">
                              {String(project.transform.color_grade).split("—")[0].trim()}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="relative">
                        {compareMode === "before" && project.video ? (
                          <video key={"before-" + project.video.storage_path} controls
                            className="w-full rounded-lg bg-black aspect-video object-contain" src={fileUrl(project.video.storage_path)} data-testid="compare-before-video" />
                        ) : (
                          <video key={"after-" + project.final_render.storage_path} controls
                            className="w-full rounded-lg bg-black aspect-video object-contain" src={fileUrl(project.final_render.storage_path)} data-testid="compare-after-video" />
                        )}
                        <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full ${compareMode === "before" ? "bg-black/70 text-white/70" : "bg-[#39FF14]/90 text-black font-semibold"}`}>
                          {compareMode === "before" ? "Original upload" : "HD 3D · Viral3D"}
                        </span>
                      </div>
                      {project.video && (
                        <p className="text-[11px] text-white/40 mt-1.5">Tap <span className="text-white/60">Before</span> / <span className="text-[#39FF14]">After</span> to see the cinematic transformation.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* REFRAME / ASPECT RESIZER */}
            <div className="bg-zinc-950 border border-white/10 rounded-lg p-6" data-testid="reframe-panel">
              <div className="flex items-center gap-2 mb-4">
                <Ratio className="w-4 h-4 text-[#00E5FF]" />
                <span className="font-display text-sm tracking-tight">Reframe for every feed</span>
              </div>
              {!hasVideo ? (
                <p className="text-sm text-white/40 font-light">Upload a video to reframe it.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-3">
                    {RATIOS.map((r) => (
                      <Button key={r.key} onClick={() => doResize(r.key)} disabled={busy[`resize_${r.key}`] || project.resize_status?.running}
                        variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white disabled:opacity-40" data-testid={`resize-btn-${r.key}`}>
                        {(busy[`resize_${r.key}`] || project.resize_status?.running) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ratio className="w-4 h-4 mr-1" />}
                        {r.label}
                        <CostHint endpoint="resize" />
                      </Button>
                    ))}
                  </div>
                  {project.resizes && Object.keys(project.resizes).length > 0 && (
                    <div className="grid sm:grid-cols-3 gap-4 mt-5 float-up">
                      {RATIOS.filter((r) => project.resizes[r.key]).map((r) => {
                        const rz = project.resizes[r.key];
                        return (
                          <div key={r.key} className="bg-black border border-white/10 rounded-lg overflow-hidden" data-testid={`resize-result-${r.key}`}>
                            <video controls className="w-full bg-black object-contain max-h-64" src={fileUrl(rz.storage_path)} />
                            <div className="p-2 flex items-center justify-between">
                              <span className="text-xs text-[#00E5FF] font-display">{r.key} · {rz.width}×{rz.height}</span>
                              <a href={fileUrl(rz.storage_path)} download className="text-white/50 hover:text-[#00E5FF]" data-testid={`download-resize-${r.key}`}>
                                <Download className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* TABS */}
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-zinc-950 border border-white/10 flex-wrap h-auto">
                <TabsTrigger value="story" data-testid="tab-story">Story</TabsTrigger>
                <TabsTrigger value="graphics" data-testid="tab-graphics">Graphics</TabsTrigger>
                <TabsTrigger value="thumbnails" data-testid="tab-thumbnails">Thumbnails</TabsTrigger>
                <TabsTrigger value="voiceover" data-testid="tab-voiceover">Voiceover</TabsTrigger>
                <TabsTrigger value="music" data-testid="tab-music">Music</TabsTrigger>
                <TabsTrigger value="captions" data-testid="tab-captions">Captions</TabsTrigger>
                <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
                <TabsTrigger value="comments" data-testid="tab-comments">Comments</TabsTrigger>
              </TabsList>

              {/* STORY */}
              <TabsContent value="story" className="mt-4">
                {!a ? <Empty text="Run the story enhancer to see your cinematic plan." /> : (
                  <div className="space-y-4 float-up" data-testid="story-panel">
                    <Panel title="Hook" accent="#00E5FF"><p className="text-lg font-display tracking-tight">{a.hook}</p></Panel>
                    <div className="bg-zinc-950 border border-white/10 rounded-lg p-5" data-testid="hooks-section">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs uppercase tracking-wider text-white/40">A/B hook variations</p>
                        <Button size="sm" variant="outline" onClick={() => run("hooks", "hook-variations", "Hook variations ready!", "story")}
                          disabled={busy.hooks} className="h-7 border-white/15 bg-transparent hover:bg-white/5 text-white text-xs" data-testid="hooks-btn">
                          {busy.hooks ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Writing…</> : <><Shuffle className="w-3.5 h-3.5 mr-1" />Generate 3</>}
                        </Button>
                      </div>
                      {project.hooks?.length ? (
                        <div className="space-y-2 mt-3">
                          {project.hooks.map((h, i) => (
                            <div key={i} className="bg-black border border-white/10 rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`hook-var-${i}`}>
                              <div>
                                <span className="text-xs text-[#39FF14]">{h.style}</span>
                                <p className="text-sm text-white/80 font-light mt-0.5">{h.text}</p>
                              </div>
                              <button onClick={() => copyText(h.text)} className="text-white/40 hover:text-white shrink-0" data-testid={`copy-hook-${i}`}>
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-white/40 font-light">Generate 3 alternate opening hooks to A/B test.</p>
                      )}
                    </div>
                    <Panel title="Story summary"><p className="text-white/70 font-light leading-relaxed">{a.story_summary}</p></Panel>
                    <div className="flex gap-3 flex-wrap">
                      {a.mood && <Badge variant="outline" className="border-[#FF007F]/40 text-[#FF007F]">Mood: {a.mood}</Badge>}
                      {a.genre && <Badge variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF]">{a.genre}</Badge>}
                    </div>
                    {a.enhancement_ideas?.length > 0 && (
                      <Panel title="Enhancement ideas">
                        <ul className="space-y-2">
                          {a.enhancement_ideas.map((idea, i) => (
                            <li key={i} className="flex gap-2 text-white/70 font-light">
                              <Check className="w-4 h-4 text-[#39FF14] shrink-0 mt-1" /> {idea}
                            </li>
                          ))}
                        </ul>
                      </Panel>
                    )}
                    {a.scene_beats?.length > 0 && (
                      <Panel title="Scene beats">
                        <div className="grid sm:grid-cols-2 gap-3">
                          {a.scene_beats.map((b, i) => (
                            <div key={i} className="bg-black border border-white/10 rounded-lg p-3">
                              <span className="text-xs text-[#00E5FF] font-display">Beat {i + 1}</span>
                              <p className="text-sm text-white/70 font-light mt-1">{b}</p>
                            </div>
                          ))}
                        </div>
                      </Panel>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* GRAPHICS */}
              <TabsContent value="graphics" className="mt-4">
                {assets.length === 0 ? <Empty text="Generate original graphics that fit your story." /> : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 float-up" data-testid="graphics-panel">
                    {assets.map((asset) => (
                      <div key={asset.id} className="group bg-zinc-950 border border-white/10 rounded-lg overflow-hidden hover:border-[#FF007F]/40 transition-colors" data-testid={`asset-${asset.id}`}>
                        <div className="aspect-square overflow-hidden">
                          <img src={fileUrl(asset.storage_path)} alt={asset.prompt}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="p-3">
                          <p className="text-xs text-white/50 font-light line-clamp-2 min-h-[32px]">{asset.prompt}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <button onClick={() => setRegen({ id: asset.id, prompt: asset.prompt })}
                              className="text-white/50 hover:text-[#FF007F] flex items-center gap-1 text-xs" data-testid={`regen-${asset.id}`}>
                              <RefreshCw className="w-3.5 h-3.5" /> Tweak
                            </button>
                            <a href={fileUrl(asset.storage_path)} download target="_blank" rel="noreferrer"
                              className="text-white/50 hover:text-[#00E5FF] flex items-center gap-1 text-xs" data-testid={`download-${asset.id}`}>
                              <Download className="w-3.5 h-3.5" /> Download
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* THUMBNAILS */}
              <TabsContent value="thumbnails" className="mt-4">
                {assets.length === 0 ? <Empty text="Generate graphics first, then create per-platform thumbnails." /> : (
                  <div className="float-up" data-testid="thumbnails-panel">
                    <div className="flex flex-wrap items-end gap-3 mb-5">
                      <div>
                        <label className="text-xs text-white/50">Source graphic</label>
                        <Select value={thumbAsset} onValueChange={setThumbAsset}>
                          <SelectTrigger className="bg-black border-white/15 mt-1 w-56" data-testid="thumb-asset-select"><SelectValue placeholder="Pick graphic" /></SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-white">
                            {assets.map((as, i) => <SelectItem key={as.id} value={as.id}>Graphic {i + 1}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={() => run("thumbnails", "thumbnails", "Thumbnails generated!", null, { asset_id: thumbAsset })}
                        disabled={busy.thumbnails || !thumbAsset}
                        className="rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium disabled:opacity-40" data-testid="thumbnails-btn">
                        {busy.thumbnails ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cropping…</> : <><Crop className="w-4 h-4 mr-1" />Generate thumbnails</>}
                      </Button>
                    </div>
                    {!project.thumbnails ? <Empty text="Pick a graphic and generate correctly-sized thumbnails for every platform." /> : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {PLATFORMS.map((p) => {
                          const th = project.thumbnails[p.key];
                          if (!th) return null;
                          return (
                            <div key={p.key} className="bg-zinc-950 border border-white/10 rounded-lg overflow-hidden" data-testid={`thumb-${p.key}`}>
                              <img src={fileUrl(th.storage_path)} alt={p.label} className="w-full aspect-video object-cover" />
                              <div className="p-3 flex items-center justify-between">
                                <div>
                                  <p className="font-display text-sm text-[#00E5FF]">{p.label}</p>
                                  <p className="text-xs text-white/40">{th.width}×{th.height}</p>
                                </div>
                                <a href={fileUrl(th.storage_path)} download className="text-white/50 hover:text-[#00E5FF]" data-testid={`download-thumb-${p.key}`}>
                                  <Download className="w-4 h-4" />
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* VOICEOVER */}
              <TabsContent value="voiceover" className="mt-4">
                {hasVideo && (
                  <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="transcribe-bar">
                    <Button onClick={transcribe} disabled={busy.transcribe} variant="outline"
                      className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white disabled:opacity-40" data-testid="transcribe-btn">
                      {busy.transcribe ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transcribing…</> : <><FileAudio className="w-4 h-4 mr-1" />Auto-caption from video audio (Whisper)</>}<CostHint endpoint="transcribe" />
                    </Button>
                    <span className="text-xs text-white/40">Turns the video's own speech into a timed script.</span>
                  </div>
                )}
                {!project.script ? <Empty text="Generate a narration script, or auto-caption from the video's audio above." /> : (
                  <div className="space-y-4 float-up" data-testid="voiceover-panel">
                    <div className="flex gap-3 flex-wrap items-center">
                      {project.script.total_duration && <Badge variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF]">Duration {project.script.total_duration}</Badge>}
                      {project.script.tone && <Badge variant="outline" className="border-[#FF007F]/40 text-[#FF007F]">Tone: {project.script.tone}</Badge>}
                      <button onClick={() => copyText((project.script.segments || []).map((s) => `[${s.start}-${s.end}] ${s.narration}`).join("\n"))}
                        className="text-white/50 hover:text-white flex items-center gap-1 text-xs" data-testid="copy-script">
                        <Copy className="w-3.5 h-3.5" /> Copy full script
                      </button>
                    </div>

                    <div className="bg-zinc-950 border border-white/10 rounded-lg p-4 space-y-4" data-testid="caption-style-panel">
                      <div className="flex items-center gap-2">
                        <Type className="w-4 h-4 text-[#00E5FF]" />
                        <span className="font-display text-sm tracking-tight">Caption style</span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <span className="text-xs text-white/50">Position</span>
                          <Select value={capPos} onValueChange={setCapPos}>
                            <SelectTrigger className="bg-black border-white/15 h-8 w-full text-xs" data-testid="caption-position-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              <SelectItem value="top">Top</SelectItem>
                              <SelectItem value="middle">Middle</SelectItem>
                              <SelectItem value="bottom">Bottom</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-white/50">Size</span>
                          <Select value={capSize} onValueChange={setCapSize}>
                            <SelectTrigger className="bg-black border-white/15 h-8 w-full text-xs" data-testid="caption-size-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              <SelectItem value="small">Small</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="large">Large</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-white/50">Font</span>
                          <Select value={capFont} onValueChange={setCapFont}>
                            <SelectTrigger className="bg-black border-white/15 h-8 w-full text-xs" data-testid="caption-font-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              {(fontOpts.length ? fontOpts : [{ key: "sans", label: "Sans" }, { key: "serif", label: "Serif" }, { key: "condensed", label: "Condensed" }, { key: "mono", label: "Mono" }]).map((f) => (
                                <SelectItem key={f.key} value={f.key} data-testid={`font-option-${f.key}`}><span style={{ fontFamily: FONT_CSS[f.key] }}>{f.label}</span></SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-white/50">Outline</span>
                          <Select value={capOutline} onValueChange={setCapOutline}>
                            <SelectTrigger className="bg-black border-white/15 h-8 w-full text-xs" data-testid="caption-outline-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="light">Light</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="heavy">Heavy</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-white/50">Karaoke</span>
                          <div className="flex items-center gap-2 h-8">
                            <Switch checked={karaoke} onCheckedChange={setKaraoke} data-testid="karaoke-toggle" />
                            {karaoke && (
                              <input type="color" value={hlColor} onChange={(e) => setHlColor(e.target.value)}
                                className="w-7 h-7 rounded-md bg-black border border-white/15 cursor-pointer" data-testid="highlight-color-input" title="Highlight color" />
                            )}
                          </div>
                        </div>
                      </div>

                      {karaoke && (
                        <div className="flex items-center gap-2" data-testid="highlight-style-row">
                          <span className="text-xs text-white/50">Highlight style</span>
                          <Select value={capHlStyle} onValueChange={setCapHlStyle}>
                            <SelectTrigger className="bg-black border-white/15 h-8 w-40 text-xs" data-testid="highlight-style-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              <SelectItem value="text">Colored text</SelectItem>
                              <SelectItem value="box">Filled box</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5" data-testid="caption-presets-row">
                        <Bookmark className="w-3.5 h-3.5 text-white/40" />
                        <Select value={presetSel} onValueChange={applyPreset}>
                          <SelectTrigger className="bg-black border-white/15 h-8 w-40 text-xs" data-testid="caption-preset-select"><SelectValue placeholder="Apply preset…" /></SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-white">
                            {presets.length === 0 ? <div className="px-2 py-1.5 text-xs text-white/40">No presets yet</div> :
                              presets.map((p) => (
                                <SelectItem key={p.id} value={p.id} data-testid={`preset-option-${p.id}`}>
                                  <span className="inline-flex items-center gap-2">
                                    <PresetChip preset={p} />
                                    <span>{p.builtin ? `★ ${p.name}` : p.name}</span>
                                  </span>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {presetSel && !presetSel.startsWith("starter-") && (
                          <Button size="sm" variant="ghost" onClick={() => deletePreset(presetSel)}
                            className="h-8 px-2 text-white/50 hover:text-[#FF007F]" data-testid="delete-preset-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
                        )}
                        <div className="flex items-center gap-1.5 ml-auto">
                          <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Save current as…"
                            className="bg-black border-white/15 h-8 w-40 text-xs" data-testid="preset-name-input" />
                          <Button size="sm" onClick={savePreset} disabled={savingPreset || !presetName.trim()}
                            className="h-8 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs disabled:opacity-40" data-testid="save-preset-btn">
                            {savingPreset ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Saving…</> : "Save preset"}
                          </Button>
                        </div>
                      </div>

                      <div className="pt-1">
                        <Button size="sm" onClick={() => run("subtitles", "subtitles", "Subtitles burned in!", "voiceover", { karaoke, position: capPos, size: capSize, font: capFont, outline: capOutline, highlight_style: capHlStyle, highlight_color: karaoke ? hlColor : undefined })}
                          disabled={busy.subtitles || !hasVideo}
                          className="w-full sm:w-auto h-9 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 text-sm font-medium disabled:opacity-40" data-testid="subtitles-btn">
                          {busy.subtitles ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Burning…</> : <><Captions className="w-4 h-4 mr-2" />Burn subtitles onto video</>}<CostHint endpoint="subtitles" />
                        </Button>
                      </div>
                    </div>
                    <div className="bg-zinc-950 border border-white/10 rounded-lg p-4" data-testid="caption-preview-panel">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <Captions className="w-4 h-4 text-[#00E5FF]" />
                        <span className="font-display text-sm tracking-tight">Live caption preview</span>
                        <span className="text-xs text-white/40 hidden lg:inline">— scales to the selected frame</span>
                        <div className="flex items-center gap-1.5 ml-auto">
                          <div className="flex items-center rounded-full border border-white/15 overflow-hidden" data-testid="aspect-toggle">
                            {["9:16", "1:1", "16:9"].map((a) => (
                              <button key={a} onClick={() => setPreviewAspect(a)} data-testid={`aspect-${a.replace(":", "-")}`}
                                className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${previewAspect === a ? "bg-[#00E5FF] text-black" : "text-white/55 hover:text-white"}`}>{a}</button>
                            ))}
                          </div>
                          <button onClick={() => setPlaying((p) => !p)} data-testid="preview-play-btn"
                            className="h-7 w-7 flex items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40" title={playing ? "Pause" : "Play through script"}>
                            {playing ? <Pause className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current" />}
                          </button>
                          <label className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer" data-testid="safe-zone-label">
                            <Switch checked={showSafe} onCheckedChange={setShowSafe} data-testid="safe-zone-toggle" />
                            Safe zones
                          </label>
                        </div>
                      </div>
                      {(() => {
                        const segs = project.script.segments || [];
                        if (segs.length <= 1) return null;
                        const idx = Math.min(previewSeg, segs.length - 1);
                        return (
                          <div className="flex items-center gap-2 mb-3" data-testid="segment-scrubber">
                            <button onClick={() => setPreviewSeg(Math.max(0, idx - 1))} disabled={idx <= 0}
                              className="h-7 w-7 flex items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 disabled:opacity-30 disabled:hover:text-white/70" data-testid="seg-prev-btn">
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <Slider value={[idx]} min={0} max={segs.length - 1} step={1}
                              onValueChange={(v) => setPreviewSeg(v[0])} className="flex-1" data-testid="seg-slider" />
                            <button onClick={() => setPreviewSeg(Math.min(segs.length - 1, idx + 1))} disabled={idx >= segs.length - 1}
                              className="h-7 w-7 flex items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 disabled:opacity-30 disabled:hover:text-white/70" data-testid="seg-next-btn">
                              <ChevronRight className="w-4 h-4" />
                            </button>
                            <span className="text-xs text-white/50 tabular-nums w-16 text-right" data-testid="seg-indicator">Line {idx + 1} / {segs.length}</span>
                          </div>
                        );
                      })()}
                      <div className="flex justify-center">
                        <div ref={previewBoxRef} className="relative rounded-lg overflow-hidden bg-black h-[360px] sm:h-[460px] max-w-full" style={{ aspectRatio: PREVIEW_ASPECTS[previewAspect] }} data-testid="caption-preview">
                          {project.video ? (
                            <video ref={previewVideoRef} key={"prev-" + project.video.storage_path} muted playsInline preload="auto"
                              className="absolute inset-0 w-full h-full object-cover" src={fileUrl(project.video.storage_path)} />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-white/35 text-xs px-6 text-center" data-testid="caption-preview-no-video">Story-only project — caption positioning preview needs a source video.</div>
                          )}
                        {showSafe && previewDims.w > 0 && (
                          <div className="absolute pointer-events-none" data-testid="safe-zone-overlay"
                            style={{ width: `${previewDims.w}px`, height: `${previewDims.h}px`, left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
                            {/* dashed unsafe-zone bounds */}
                            <div className="absolute left-0 right-0 top-0 border-b border-dashed border-[#FF007F]/50" style={{ height: "8%" }} />
                            <div className="absolute left-0 right-0 bottom-0 border-t border-dashed border-[#FF007F]/50" style={{ height: "22%" }} />
                            <div className="absolute right-0 border-l border-dashed border-[#FF007F]/50" style={{ width: "16%", top: "30%", height: "52%" }} />
                            {/* mock top bar */}
                            <div className="absolute left-0 right-0 top-0 flex items-center justify-center gap-4 text-white/70 text-[10px] font-medium" style={{ height: "8%" }}>
                              <span>Following</span><span className="text-white">For You</span>
                            </div>
                            {/* mock right action rail */}
                            <div className="absolute right-0 flex flex-col items-center justify-center gap-3 text-white" style={{ width: "16%", top: "30%", height: "52%" }}>
                              <div className="w-8 h-8 rounded-full bg-white/15 border border-white/40 flex items-center justify-center"><User className="w-4 h-4" /><span className="absolute -bottom-1 w-3.5 h-3.5 rounded-full bg-[#FF007F] flex items-center justify-center"><Plus className="w-2.5 h-2.5" /></span></div>
                              <div className="flex flex-col items-center"><Heart className="w-6 h-6 fill-white/90" /><span className="text-[9px]">128K</span></div>
                              <div className="flex flex-col items-center"><MessageSquare className="w-6 h-6 fill-white/90" /><span className="text-[9px]">2.4K</span></div>
                              <div className="flex flex-col items-center"><Bookmark className="w-6 h-6 fill-white/90" /><span className="text-[9px]">Save</span></div>
                              <div className="flex flex-col items-center"><Share2 className="w-6 h-6" /><span className="text-[9px]">Share</span></div>
                              <div className="w-8 h-8 rounded-full bg-black/60 border border-white/30 flex items-center justify-center animate-spin" style={{ animationDuration: "4s" }}><Music2 className="w-4 h-4" /></div>
                            </div>
                            {/* mock bottom meta */}
                            <div className="absolute left-2 bottom-1 max-w-[70%] text-white" style={{ height: "22%" }}>
                              <div className="flex flex-col justify-end h-full pb-1 gap-0.5">
                                <span className="text-[11px] font-bold">@yourbrand</span>
                                <span className="text-[10px] text-white/80 leading-tight">Your caption &amp; #hashtags sit here ✨</span>
                                <span className="flex items-center gap-1 text-[9px] text-white/70"><Music2 className="w-3 h-3" /> original sound — yourbrand</span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className={`absolute inset-0 flex justify-center pointer-events-none ${
                          capPos === "top" ? "items-start pt-4" : capPos === "middle" ? "items-center" : "items-end pb-5"
                        }`}>
                          {(() => {
                            const segs = project.script.segments || [];
                            const previewText = (segs[Math.min(previewSeg, Math.max(segs.length - 1, 0))]?.narration) || "Your captions appear here";
                            const baseStyle = { fontSize: `${capFontPx}px`, maxWidth: capMaxW ? `${capMaxW}px` : "90%", fontFamily: FONT_CSS[capFont] };
                            if (karaoke && capHlStyle === "box") {
                              return (
                                <span data-testid="caption-preview-text"
                                  className="font-bold text-center leading-tight break-words px-2 py-0.5 rounded-md"
                                  style={{ ...baseStyle, color: "#151515", backgroundColor: hlColor, boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}>
                                  {previewText}
                                </span>
                              );
                            }
                            return (
                              <span data-testid="caption-preview-text"
                                className="font-bold text-center leading-tight break-words"
                                style={{ ...baseStyle, color: "#FFFFFF", WebkitTextStroke: `${OUTLINE_CSS[capOutline].stroke} #151515`, textShadow: OUTLINE_CSS[capOutline].shadow }}>
                                {!karaoke ? previewText : previewText.split(/\s+/).filter(Boolean).map((wd, wi) => (
                                  <span key={wi} style={{ color: wi <= kIdx ? hlColor : "rgba(255,255,255,0.82)", transition: "color 120ms ease" }}>{wd}{" "}</span>
                                ))}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/5" data-testid="reframe-export-row">
                        <span className="text-xs text-white/40 w-full sm:w-auto">Match the exported video to this frame:</span>
                        <Button size="sm" onClick={() => doResize(previewAspect)} disabled={busy[`resize_${previewAspect}`] || busy.resize_all || project.resize_status?.running || !hasVideo}
                          className="h-8 rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 text-xs font-medium disabled:opacity-40" data-testid="reframe-export-btn">
                          {(busy[`resize_${previewAspect}`] || project.resize_status?.running) ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Reframing…</> : <><Ratio className="w-3.5 h-3.5 mr-1" />Reframe &amp; export to {previewAspect}</>}<CostHint endpoint="resize" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={reframeAll} disabled={busy.resize_all || project.resize_status?.running || !hasVideo}
                          className="h-8 rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white text-xs disabled:opacity-40" data-testid="batch-reframe-btn">
                          {(busy.resize_all || project.resize_status?.running) ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Exporting all…</> : "Export all 3 aspects"}<CostHint endpoint="reframe-all" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={captureCover} disabled={busy.cover || !hasVideo}
                          className="h-8 rounded-full border-[#00E5FF]/40 bg-transparent hover:bg-[#00E5FF]/10 text-[#00E5FF] text-xs disabled:opacity-40" data-testid="grab-cover-btn">
                          {busy.cover ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Capturing…</> : <><ImageIcon className="w-3.5 h-3.5 mr-1" />Grab cover frame</>}<CostHint endpoint="cover-frame" />
                        </Button>
                        {project.resizes && project.resizes[previewAspect] && (
                          <a href={fileUrl(project.resizes[previewAspect].storage_path)} download
                            className="text-[#39FF14] hover:text-[#39FF14]/80 flex items-center gap-1 text-xs" data-testid="reframe-export-download">
                            <Download className="w-3.5 h-3.5" /> {previewAspect} ready
                          </a>
                        )}
                      </div>
                    </div>
                    {project.subtitled_video && (
                      <div className="bg-zinc-950 border border-white/10 rounded-lg p-4" data-testid="subtitled-result">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[#39FF14]">Subtitled video</span>
                          <a href={fileUrl(project.subtitled_video.storage_path)} download className="text-white/50 hover:text-[#00E5FF] flex items-center gap-1 text-xs" data-testid="download-subtitled">
                            <Download className="w-3.5 h-3.5" /> Download
                          </a>
                        </div>
                        <video key={project.subtitled_video.storage_path} controls
                          className="w-full rounded-lg bg-black aspect-video object-contain" src={fileUrl(project.subtitled_video.storage_path)} />
                      </div>
                    )}
                    <div className="bg-zinc-950 border border-white/10 rounded-lg p-4" data-testid="voiceover-audio-panel">
                      <div className="flex items-center gap-2 mb-3">
                        <AudioLines className="w-4 h-4 text-[#39FF14]" />
                        <span className="font-display text-sm tracking-tight">AI voice track</span>
                      </div>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="text-xs text-white/50">Engine</label>
                          <Select value={ttsEngine} onValueChange={setTtsEngine}>
                            <SelectTrigger className="bg-black border-white/15 mt-1 w-40" data-testid="tts-engine-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              <SelectItem value="openai">Standard (OpenAI)</SelectItem>
                              <SelectItem value="elevenlabs" disabled={voiceCatalog && !voiceCatalog.elevenlabs?.available}>
                                ElevenLabs — Premium{voiceCatalog && !voiceCatalog.elevenlabs?.available ? " (add key)" : ""}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {ttsEngine === "openai" ? (
                          <div>
                            <label className="text-xs text-white/50">Voice</label>
                            <Select value={voice} onValueChange={setVoice}>
                              <SelectTrigger className="bg-black border-white/15 mt-1 w-36 capitalize" data-testid="voice-select"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                {VOICES.map((v) => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div>
                            <label className="text-xs text-white/50">Premium voice</label>
                            <Select value={elVoice} onValueChange={setElVoice}>
                              <SelectTrigger className="bg-black border-white/15 mt-1 w-44" data-testid="el-voice-select"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                {(voiceCatalog?.elevenlabs?.voices || []).map((v) => (
                                  <SelectItem key={v.id} value={v.id}>{v.name}{v.desc ? ` — ${v.desc}` : ""}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <Button onClick={previewVoice} disabled={previewing} variant="outline" size="icon"
                          className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white h-10 w-10 shrink-0" data-testid="voice-preview-btn" title="Preview voice">
                          {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                        </Button>
                        <Button onClick={() => run("vaudio", "voiceover-audio", "Voice track generated!", "voiceover",
                            ttsEngine === "elevenlabs"
                              ? { engine: "elevenlabs", voice_id: elVoice }
                              : { engine: "openai", voice, model: "tts-1" })}
                          disabled={busy.vaudio}
                          className="rounded-full bg-[#39FF14] text-black hover:bg-[#39FF14]/90 font-medium disabled:opacity-40" data-testid="voiceover-audio-btn">
                          {busy.vaudio ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Synthesizing…</> : <><Mic className="w-4 h-4 mr-1" />Generate voice</>}<CostHint endpoint="voiceover-audio" />
                        </Button>
                        {project.voiceover_audio && (
                          <Button onClick={() => run("attach", "attach-audio", "Voice attached to video!", "voiceover")}
                            disabled={busy.attach} variant="outline"
                            className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white disabled:opacity-40" data-testid="attach-audio-btn">
                            {busy.attach ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Attaching…</> : "Attach to video"}
                          </Button>
                        )}
                      </div>
                      {project.voiceover_audio && (
                        <audio controls className="w-full mt-3" src={fileUrl(project.voiceover_audio.storage_path)} data-testid="voiceover-audio-player" />
                      )}
                      {project.voiceover_audio && (
                        <div className="mt-4 pt-4 border-t border-white/10" data-testid="music-bed-block">
                          <div className="flex items-center gap-2 mb-2">
                            <Disc3 className="w-4 h-4 text-[#FF007F]" />
                            <span className="font-display text-sm tracking-tight">Auto music bed</span>
                          </div>
                          <p className="text-xs text-white/40 mb-3">Upload a royalty-free track — we mix it under the voice automatically.</p>
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="w-48">
                              <label className="text-xs text-white/50">Music volume: {Math.round(musicVol * 100)}%</label>
                              <Slider min={0.05} max={0.6} step={0.01} value={[musicVol]} onValueChange={(v) => setMusicVol(v[0])}
                                className="mt-2" data-testid="music-volume-slider" />
                            </div>
                            <input ref={musicRef} type="file" accept="audio/*" className="hidden" onChange={uploadMusic} data-testid="music-file-input" />
                            <Button onClick={() => musicRef.current?.click()} disabled={musicUploading} variant="outline"
                              className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white" data-testid="music-bed-btn">
                              {musicUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Mixing…</> : <><Music2 className="w-4 h-4 mr-1" />Upload & mix track</>}
                            </Button>
                          </div>
                          <div className="mt-4" data-testid="music-library">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                              <p className="text-xs text-white/50">Or pick a built-in royalty-free bed:</p>
                              <div className="flex items-center gap-2">
                                <Select value={project.analysis?.mood || ""} onValueChange={saveMood}>
                                  <SelectTrigger className="bg-black border-white/15 h-7 w-32 text-xs" data-testid="mood-select"><SelectValue placeholder="Mood" /></SelectTrigger>
                                  <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                    {["calm", "energetic", "dreamy", "epic", "moody"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Button size="sm" onClick={autoPickBed} disabled={busy.autobed || !project.analysis}
                                  className="h-7 rounded-full bg-[#39FF14] text-black hover:bg-[#39FF14]/90 text-xs font-medium disabled:opacity-40" data-testid="mood-autopick-btn">
                                  {busy.autobed ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" />Auto-pick</>}
                                </Button>
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2">
                              {libraryTracks.map((tr) => (
                                <div key={tr.id} className="flex items-center gap-2 bg-black border border-white/10 rounded-lg p-2" data-testid={`lib-track-${tr.id}`}>
                                  <audio src={fileUrl(tr.storage_path)} controls className="h-8 flex-1 min-w-0" style={{ maxWidth: 160 }} />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm truncate">{tr.name}</p>
                                    <p className="text-xs text-white/40 truncate">{tr.mood}</p>
                                  </div>
                                  <Button size="sm" onClick={() => applyLibraryBed(tr.id)} disabled={busy[`lib_${tr.id}`]}
                                    className="h-7 rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 text-xs shrink-0" data-testid={`use-lib-${tr.id}`}>
                                    {busy[`lib_${tr.id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Use"}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                          {project.music_bed_audio && (
                            <audio controls className="w-full mt-3" src={fileUrl(project.music_bed_audio.storage_path)} data-testid="music-bed-player" />
                          )}
                        </div>
                      )}
                      {project.narrated_video && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-[#39FF14]">Video with voiceover</span>
                            <a href={fileUrl(project.narrated_video.storage_path)} download className="text-white/50 hover:text-[#00E5FF] flex items-center gap-1 text-xs" data-testid="download-narrated">
                              <Download className="w-3.5 h-3.5" /> Download
                            </a>
                          </div>
                          <video key={project.narrated_video.storage_path} controls
                            className="w-full rounded-lg bg-black aspect-video object-contain" src={fileUrl(project.narrated_video.storage_path)} />
                        </div>
                      )}
                    </div>
                    {editScript === null ? (
                      <div>
                        <div className="flex justify-end mb-2">
                          <Button size="sm" variant="outline" onClick={startEditScript}
                            className="h-7 border-white/15 bg-transparent hover:bg-white/5 text-white text-xs" data-testid="edit-captions-btn">
                            <Scissors className="w-3.5 h-3.5 mr-1" /> Edit captions
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {(project.script.segments || []).map((s, i) => (
                            <div key={i} className="bg-zinc-950 border border-white/10 rounded-lg p-4 flex gap-4" data-testid={`script-seg-${i}`}>
                              <div className="shrink-0 text-center">
                                <div className="font-display text-[#39FF14] text-sm">{s.start}</div>
                                <div className="text-xs text-white/30">↓</div>
                                <div className="font-display text-white/50 text-sm">{s.end}</div>
                              </div>
                              <div>
                                <p className="text-xs text-[#00E5FF] uppercase tracking-wider mb-1">{s.beat}</p>
                                <p className="text-white/80 font-light leading-relaxed">{s.narration}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="caption-editor">
                        {editScript.map((s, i) => (
                          <div key={i} className="bg-zinc-950 border border-white/10 rounded-lg p-3" data-testid={`edit-seg-${i}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Input value={s.start} onChange={(e) => updateSeg(i, { start: e.target.value })}
                                className="bg-black border-white/15 h-8 w-20 text-xs text-center" data-testid={`edit-start-${i}`} />
                              <span className="text-white/30 text-xs">→</span>
                              <Input value={s.end} onChange={(e) => updateSeg(i, { end: e.target.value })}
                                className="bg-black border-white/15 h-8 w-20 text-xs text-center" data-testid={`edit-end-${i}`} />
                              <Input value={s.beat} onChange={(e) => updateSeg(i, { beat: e.target.value })}
                                placeholder="Label" className="bg-black border-white/15 h-8 flex-1 text-xs" data-testid={`edit-beat-${i}`} />
                              <button onClick={() => removeSeg(i)} className="text-white/40 hover:text-[#FF007F] shrink-0" data-testid={`edit-remove-${i}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <Textarea value={s.narration} onChange={(e) => updateSeg(i, { narration: e.target.value })}
                              className="bg-black border-white/15 min-h-[56px] text-sm focus-visible:ring-[#00E5FF]" data-testid={`edit-narration-${i}`} />
                          </div>
                        ))}
                        <div className="flex items-center gap-3 flex-wrap">
                          <Button size="sm" variant="outline" onClick={addSeg} className="border-white/15 bg-transparent hover:bg-white/5 text-white" data-testid="edit-add-seg-btn">+ Add line</Button>
                          <div className="flex-1" />
                          <Button size="sm" variant="ghost" onClick={() => setEditScript(null)} className="text-white/60 hover:text-white" data-testid="edit-cancel-btn">Cancel</Button>
                          <Button size="sm" onClick={saveScript} disabled={busy.savescript}
                            className="rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium" data-testid="edit-save-btn">
                            {busy.savescript ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save captions"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* MUSIC */}
              <TabsContent value="music" className="mt-4">
                <div className="float-up" data-testid="music-panel">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-white/50 font-light">Copyright-safe track ideas matched to your video's mood & pacing.</p>
                    <Button onClick={() => run("music", "music-suggestions", "Music suggestions ready!", "music")}
                      disabled={!a || busy.music}
                      className="rounded-full bg-white text-black hover:bg-white/90 font-medium disabled:opacity-40" data-testid="music-btn">
                      {busy.music ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Curating…</> : <><Music2 className="w-4 h-4 mr-1" />Suggest music</>}
                    </Button>
                  </div>
                  {!project.music?.tracks?.length ? <Empty text="Get 5 copyright-safe track recommendations for this video." /> : (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {project.music.tracks.map((tr, i) => (
                        <div key={i} className="bg-zinc-950 border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors" data-testid={`music-track-${i}`}>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                              <Music2 className="w-4 h-4 text-[#39FF14]" />
                            </div>
                            <div>
                              <p className="font-display text-sm tracking-tight">{tr.title}</p>
                              <p className="text-xs text-white/40">{tr.style}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap mb-2">
                            {tr.mood && <Badge variant="outline" className="border-[#FF007F]/40 text-[#FF007F] text-xs">{tr.mood}</Badge>}
                            {tr.bpm && <Badge variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF] text-xs">{tr.bpm} BPM</Badge>}
                            {tr.genre && <Badge variant="outline" className="border-white/20 text-white/60 text-xs">{tr.genre}</Badge>}
                          </div>
                          <p className="text-sm text-white/60 font-light">{tr.why_it_fits}</p>
                          {tr.source && <p className="text-xs text-[#39FF14] mt-2">Find on: {tr.source}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* CAPTIONS */}
              <TabsContent value="captions" className="mt-4">
                {!project.captions ? <Empty text="Generate platform-native captions & hashtags." /> : (
                  <div className="grid sm:grid-cols-2 gap-4 float-up" data-testid="captions-panel">
                    {PLATFORMS.map((p) => {
                      const c = project.captions[p.key];
                      if (!c) return null;
                      const tags = (c.hashtags || []).map((h) => `#${h}`).join(" ");
                      const full = `${c.caption}\n\n${tags}`;
                      return (
                        <div key={p.key} className="bg-zinc-950 border border-white/10 rounded-lg p-4" data-testid={`caption-${p.key}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-display text-sm tracking-tight text-[#00E5FF]">{p.label}</span>
                            <div className="flex items-center gap-3">
                              <a href={exportUrl(id, p.key)} download className="text-white/40 hover:text-[#39FF14] flex items-center gap-1 text-xs" data-testid={`export-${p.key}`}>
                                <Package className="w-3.5 h-3.5" /> Export
                              </a>
                              <button onClick={() => copyText(full)} className="text-white/40 hover:text-white" data-testid={`copy-${p.key}`}>
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-white/70 font-light whitespace-pre-wrap">{c.caption}</p>
                          <p className="text-xs text-[#FF007F] mt-3 break-words">{tags}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
              {/* SCHEDULE */}
              <TabsContent value="schedule" className="mt-4">
                <div className="float-up" data-testid="schedule-panel">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <p className="text-sm text-white/50 font-light">Plan each platform post with its caption and time. (Planner — does not auto-publish.)</p>
                    <div className="flex items-center gap-1 bg-black border border-white/10 rounded-full p-1">
                      <button onClick={() => setSchedView("list")} className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 ${schedView === "list" ? "bg-[#00E5FF] text-black" : "text-white/60"}`} data-testid="sched-view-list">
                        <List className="w-3.5 h-3.5" /> List
                      </button>
                      <button onClick={() => setSchedView("calendar")} className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 ${schedView === "calendar" ? "bg-[#00E5FF] text-black" : "text-white/60"}`} data-testid="sched-view-calendar">
                        <CalendarDays className="w-3.5 h-3.5" /> Calendar
                      </button>
                    </div>
                  </div>
                  <div className="bg-zinc-950 border border-white/10 rounded-lg p-5 mb-5">
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-white/50">Platform</label>
                        <Select value={schedForm.platform} onValueChange={(v) => setSchedForm({ ...schedForm, platform: v })}>
                          <SelectTrigger className="bg-black border-white/15 mt-1" data-testid="sched-platform-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-white/10 text-white">
                            {PLATFORMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-white/50">Date & time</label>
                        <Input type="datetime-local" value={schedForm.scheduled_at}
                          onChange={(e) => setSchedForm({ ...schedForm, scheduled_at: e.target.value })}
                          className="bg-black border-white/15 mt-1 focus-visible:ring-[#00E5FF]" data-testid="sched-datetime-input" />
                      </div>
                      <div className="flex items-end">
                        <Button onClick={addSchedule} disabled={busy.sched}
                          className="w-full rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium" data-testid="sched-add-btn">
                          {busy.sched ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarClock className="w-4 h-4 mr-1" />Schedule</>}
                        </Button>
                      </div>
                    </div>
                    <Textarea value={schedForm.caption} onChange={(e) => setSchedForm({ ...schedForm, caption: e.target.value })}
                      placeholder="Caption for this post (optional)…" className="bg-black border-white/15 mt-3 focus-visible:ring-[#00E5FF]" data-testid="sched-caption-input" />
                    <div className="flex items-center gap-4 mt-3 flex-wrap">
                      <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
                        <Switch checked={schedForm.repeat === "weekly"}
                          onCheckedChange={(v) => setSchedForm({ ...schedForm, repeat: v ? "weekly" : "none" })} data-testid="sched-repeat-toggle" />
                        Repeat weekly
                      </label>
                      {schedForm.repeat === "weekly" && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/50">for</span>
                          <Select value={String(schedForm.repeat_count)} onValueChange={(v) => setSchedForm({ ...schedForm, repeat_count: Number(v) })}>
                            <SelectTrigger className="bg-black border-white/15 h-8 w-20 text-xs" data-testid="sched-repeat-count"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              {[2, 3, 4, 6, 8, 12].map((n) => <SelectItem key={n} value={String(n)}>{n} weeks</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-zinc-950 border border-white/10 rounded-lg p-5 mb-5" data-testid="templates-card">
                    <div className="flex items-center gap-2 mb-3">
                      <CalendarClock className="w-4 h-4 text-[#39FF14]" />
                      <span className="font-display text-sm tracking-tight">Series templates</span>
                    </div>
                    <div className="flex flex-wrap items-end gap-3 mb-3">
                      <div className="flex-1 min-w-[160px]">
                        <label className="text-xs text-white/50">Save current schedule as</label>
                        <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Launch week"
                          className="bg-black border-white/15 mt-1 focus-visible:ring-[#39FF14]" data-testid="tpl-name-input" />
                      </div>
                      <Button onClick={saveTemplate} variant="outline"
                        className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white" data-testid="tpl-save-btn">Save template</Button>
                    </div>
                    {templates.length > 0 && (
                      <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-white/10">
                        <div className="min-w-[160px]">
                          <label className="text-xs text-white/50">Apply template</label>
                          <Select value={tplSel} onValueChange={setTplSel}>
                            <SelectTrigger className="bg-black border-white/15 mt-1 w-44" data-testid="tpl-select"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10 text-white">
                              {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.items.length})</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-white/50">Starting</label>
                          <Input type="date" value={tplStart} onChange={(e) => setTplStart(e.target.value)}
                            className="bg-black border-white/15 mt-1 focus-visible:ring-[#39FF14]" data-testid="tpl-start-input" />
                        </div>
                        <Button onClick={applyTemplate} disabled={busy.applytpl}
                          className="rounded-full bg-[#39FF14] text-black hover:bg-[#39FF14]/90 font-medium" data-testid="tpl-apply-btn">
                          {busy.applytpl ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                        </Button>
                        {tplSel && (
                          <button onClick={() => delTemplate(tplSel)} className="text-white/40 hover:text-[#FF007F] pb-2" data-testid="tpl-del-btn">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {schedView === "list" && (!(project.schedule?.length) ? (
                    <Empty text="No scheduled posts yet. Plan your rollout across platforms." />
                  ) : (
                    <div className="space-y-2" data-testid="schedule-list">
                      {project.schedule.map((s) => {
                        const label = PLATFORMS.find((p) => p.key === s.platform)?.label || s.platform;
                        return (
                          <div key={s.id} className="bg-zinc-950 border border-white/10 rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`sched-item-${s.id}`}>
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="text-center shrink-0">
                                <CalendarClock className="w-5 h-5 text-[#39FF14] mx-auto" />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF]">{label}</Badge>
                                  <span className="text-sm text-white/70">{new Date(s.scheduled_at).toLocaleString()}</span>
                                </div>
                                {s.caption && <p className="text-xs text-white/40 mt-1 truncate">{s.caption}</p>}
                              </div>
                            </div>
                            <button onClick={() => delSchedule(s.id)} className="text-white/40 hover:text-[#FF007F] shrink-0" data-testid={`sched-del-${s.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {schedView === "calendar" && (
                    <div className="grid md:grid-cols-2 gap-6" data-testid="schedule-calendar">
                      <div className="bg-zinc-950 border border-white/10 rounded-lg p-4 flex justify-center">
                        <Calendar mode="single" selected={calDay} onSelect={(d) => d && setCalDay(d)}
                          modifiers={{ scheduled: (project.schedule || []).map((s) => new Date(s.scheduled_at)) }}
                          modifiersClassNames={{ scheduled: "bg-[#00E5FF]/25 text-[#00E5FF] font-bold rounded-full" }} />
                      </div>
                      <div>
                        <p className="text-xs text-white/50 mb-2">{calDay.toDateString()}</p>
                        <div className="space-y-2">
                          {(project.schedule || []).filter((s) => new Date(s.scheduled_at).toDateString() === calDay.toDateString()).map((s) => {
                            const label = PLATFORMS.find((p) => p.key === s.platform)?.label || s.platform;
                            return (
                              <div key={s.id} className="bg-zinc-950 border border-white/10 rounded-lg p-3 flex items-center justify-between" data-testid={`cal-item-${s.id}`}>
                                <div>
                                  <Badge variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF]">{label}</Badge>
                                  <p className="text-xs text-white/50 mt-1">{new Date(s.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                                </div>
                                <button onClick={() => delSchedule(s.id)} className="text-white/40 hover:text-[#FF007F]" data-testid={`cal-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                              </div>
                            );
                          })}
                          {(project.schedule || []).filter((s) => new Date(s.scheduled_at).toDateString() === calDay.toDateString()).length === 0 && (
                            <p className="text-sm text-white/30">No posts on this day.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* COMMENTS */}
              <TabsContent value="comments" className="mt-4">
                <div className="float-up" data-testid="comments-panel">
                  <div className="bg-zinc-950 border border-white/10 rounded-lg p-4 mb-4">
                    <Textarea value={commentText} onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Leave a note for your team… use @name to mention" className="bg-black border-white/15 focus-visible:ring-[#00E5FF]" data-testid="comment-input" />
                    {members.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="mention-hints">
                        <span className="text-xs text-white/30">Mention:</span>
                        {members.map((m) => {
                          const handle = (m.name || "").split(" ")[0];
                          return (
                            <button key={m.id} type="button"
                              onClick={() => setCommentText((t) => `${t}${t && !t.endsWith(" ") ? " " : ""}@${handle} `)}
                              className="text-xs text-[#00E5FF] hover:underline" data-testid={`mention-chip-${m.id}`}>@{handle}</button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                      <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer">
                        <Switch checked={pinTime} onCheckedChange={setPinTime} data-testid="comment-pin-toggle" />
                        Pin to current video time {pinTime && videoRef.current ? `(${fmtSecs(videoRef.current.currentTime || 0)})` : ""}
                      </label>
                      <Button onClick={addComment} disabled={busy.comment || !commentText.trim()}
                        className="rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium disabled:opacity-40" data-testid="comment-add-btn">
                        {busy.comment ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageSquare className="w-4 h-4 mr-1" />Post note</>}
                      </Button>
                    </div>
                  </div>
                  {!(project.comments?.length) ? (
                    <Empty text="No notes yet. Leave time-stamped feedback for your team." />
                  ) : (
                    <div className="space-y-2" data-testid="comments-list">
                      {[...project.comments].reverse().map((c) => {
                        const mentionedMe = (c.mentions || []).some((m) => m.id === authUser?.id);
                        return (
                        <div key={c.id} className={`bg-zinc-950 border rounded-lg p-4 flex justify-between gap-3 ${mentionedMe ? "border-[#39FF14]/50" : "border-white/10"}`} data-testid={`comment-${c.id}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-display text-[#00E5FF]">{c.user_name}</span>
                              {mentionedMe && <Badge variant="outline" className="border-[#39FF14]/50 text-[#39FF14] text-xs" data-testid={`mentioned-me-${c.id}`}>Mentioned you</Badge>}
                              {c.at_seconds != null && (
                                <button onClick={() => { if (videoRef.current) { videoRef.current.currentTime = c.at_seconds; videoRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } }}
                                  className="text-xs text-[#39FF14] flex items-center gap-1 hover:underline" data-testid={`comment-seek-${c.id}`}>
                                  <Play className="w-3 h-3" /> {fmtSecs(c.at_seconds)}
                                </button>
                              )}
                              <span className="text-xs text-white/30">{new Date(c.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-white/70 font-light mt-1">{c.text}</p>
                            {(c.mentions || []).length > 0 && (
                              <div className="flex gap-1.5 mt-2 flex-wrap">
                                {c.mentions.map((m) => (
                                  <span key={m.id} className="text-xs px-2 py-0.5 rounded-full bg-[#00E5FF]/10 text-[#00E5FF]">@{(m.name || "").split(" ")[0]}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button onClick={() => delComment(c.id)} className="text-white/40 hover:text-[#FF007F] shrink-0" data-testid={`comment-del-${c.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Regenerate dialog */}
      <Dialog open={!!regen} onOpenChange={(o) => !o && setRegen(null)}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">Tweak & regenerate</DialogTitle>
            <DialogDescription className="text-white/50">Edit the prompt and regenerate this graphic until it feels perfect.</DialogDescription>
          </DialogHeader>
          <Textarea value={regen?.prompt || ""} onChange={(e) => setRegen((r) => ({ ...r, prompt: e.target.value }))}
            className="bg-black border-white/15 min-h-[120px] focus-visible:ring-[#FF007F]" data-testid="regen-prompt-input" />
          <DialogFooter>
            <Button onClick={doRegen} disabled={busy.regen}
              className="w-full rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 font-medium" data-testid="regen-confirm-btn">
              {busy.regen ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Regenerating…</> : "Regenerate graphic"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PipelineStep = ({ n, icon: Icon, title, done, children }) => (
  <div className="bg-zinc-950 border border-white/10 rounded-lg p-4">
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${done ? "bg-[#39FF14]/10 border-[#39FF14]/40" : "bg-white/5 border-white/10"}`}>
        {done ? <Check className="w-4 h-4 text-[#39FF14]" /> : <Icon className="w-4 h-4 text-[#00E5FF]" />}
      </div>
      <div>
        <p className="text-xs text-white/40">Step {n}</p>
        <p className="font-display text-sm tracking-tight">{title}</p>
      </div>
    </div>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="bg-black border border-white/10 rounded-lg p-3">
    <p className="text-xs text-white/40">{label}</p>
    <p className="text-sm text-white/80 font-light mt-0.5 line-clamp-2">{value}</p>
  </div>
);

const Panel = ({ title, accent, children }) => (
  <div className="bg-zinc-950 border border-white/10 rounded-lg p-5">
    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: accent || "rgba(255,255,255,0.4)" }}>{title}</p>
    {children}
  </div>
);

const Empty = ({ text }) => (
  <div className="border border-dashed border-white/15 rounded-lg py-16 text-center text-white/40">
    <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#00E5FF]" />
    <p className="font-light">{text}</p>
  </div>
);
